<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div v-if="!canUseDoctorCenter" class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="alert alert-warning mb-0">You do not have access to the appointment doctor center.</div>
          </div>
        </div>
      </div>
    </div>

    <template v-else>
      <div v-if="!isCreateAvailabilityPage" class="row">
        <div class="col-12">
          <div class="card availability-create-card">
            <div class="card-body">
              <div class="section-head section-head-inline">
                <div>
                  <h4 class="card-title mb-1">Create Availability</h4>
                  <p class="text-muted mb-0">Open the guided page to submit one-time or recurring availability without crowding the doctor center.</p>
                </div>
                <b-button variant="primary" class="ms-auto" @click="openCreateAvailabilityModal">
                  <i class="mdi mdi-calendar-plus-outline me-1"></i>
                  Create Availability
                </b-button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="isCreateAvailabilityPage" class="row">
        <div class="col-12">
          <div class="create-availability-page-shell">
            <div class="wizard-progress wizard-progress-page mb-4">
              <div class="wizard-stepper">
                <div
                  v-for="step in activeWizardSteps"
                  :key="step.number"
                  class="wizard-stepper-item"
                  :class="{
                    active: step.number === createWizardStep,
                    complete: step.number < createWizardStep,
                  }"
                >
                  <span class="wizard-stepper-number">{{ step.number }}</span>
                  <span class="wizard-stepper-copy">
                    <strong>{{ step.short }}</strong>
                    <small>{{ step.title }}</small>
                  </span>
                </div>
              </div>
              <div class="wizard-progress-track">
                <span class="wizard-progress-fill" :style="{ width: wizardProgressWidth }"></span>
              </div>
            </div>

            <form class="wizard-form wizard-form-page" @submit.prevent="handleCreateWizardPrimaryAction">
              <div class="wizard-page-layout" :class="{ 'wizard-page-layout--with-summary': showWizardSummaryCard }">
                <div class="wizard-page-main">
                  <transition name="wizard-fade" mode="out-in">
                    <div :key="wizardPaneKey">
                      <div v-if="createWizardStep === 1" class="wizard-pane wizard-stage">
                        <div class="wizard-card wizard-card-compact wizard-card-service-step">
                          <div class="wizard-card-head">
                            <span class="wizard-card-kicker">Step 1</span>
                            <h6>Choose a Service</h6>
                            <p>Pick the bookable service first. The form will guide you to the next step immediately.</p>
                          </div>
                          <label class="form-label">Bookable Service</label>
                          <multiselect
                            v-model="selectedService"
                            :options="services"
                            label="service_name"
                            track-by="id"
                            placeholder="Search and select bookable service"
                            :custom-label="serviceLabel"
                            :class="{ 'is-invalid-field': validationErrors.appointment_service_id }"
                            @input="handleServiceSelection"
                          ></multiselect>
                          <small v-if="validationErrors.appointment_service_id" class="text-danger">{{ validationErrors.appointment_service_id }}</small>
                        </div>
                      </div>

                      <div v-else-if="createWizardStep === 2" class="wizard-pane wizard-stage">
                        <div class="wizard-card wizard-card-compact wizard-card-service-step">
                          <div class="wizard-card-head">
                            <span class="wizard-card-kicker">Step 2</span>
                            <h6>Choose an Availability Type</h6>
                            <p>Select whether this is a one-time window or a recurring weekly pattern.</p>
                          </div>
                          <div class="availability-type-row">
                            <label class="availability-type-card stacked" :class="{ active: form.type === 'recurring' }" @click.prevent="selectAvailabilityType('recurring')">
                              <span class="availability-option-badge">Recommended</span>
                              <input :checked="form.type === 'recurring'" type="radio" value="recurring">
                              <span class="type-radio"></span>
                              <span class="type-copy">
                                <strong>Recurring</strong>
                                <small>Choose a date range first, then paint the weekly availability sheet.</small>
                              </span>
                            </label>

                            <label class="availability-type-card stacked" :class="{ active: form.type === 'one_off' }" @click.prevent="selectAvailabilityType('one_off')">
                              <input :checked="form.type === 'one_off'" type="radio" value="one_off">
                              <span class="type-radio"></span>
                              <span class="type-copy">
                                <strong>One Time</strong>
                                <small>Choose one date and time window, then add breaks if needed.</small>
                              </span>
                            </label>
                          </div>
                          <small v-if="validationErrors.type" class="text-danger">{{ validationErrors.type }}</small>
                        </div>
                      </div>

                      <div v-else-if="form.type === 'one_off' && createWizardStep === 3" class="wizard-pane wizard-stage">
                        <div class="wizard-card wizard-card-medium one-off-time-card">
                          <div class="wizard-card-head">
                            <span class="wizard-card-kicker">Step 3</span>
                            <h6>Choose a Time Range</h6>
                            <p>Set the exact date and time window for this one-time availability, then add any break windows inside it.</p>
                          </div>
                          <div class="one-off-layout">
                            <div class="time-range-field one-off-date-field" :class="{ invalid: validationErrors.specific_date }">
                              <label class="form-label">Availability Date</label>
                              <b-form-input
                                v-model="form.specific_date"
                                type="date"
                                :min="todayIso"
                                :class="{ 'is-invalid': validationErrors.specific_date }"
                              ></b-form-input>
                              <small v-if="validationErrors.specific_date" class="text-danger">{{ validationErrors.specific_date }}</small>
                            </div>

                            <div class="time-range-grid one-off-time-grid">
                              <div class="time-range-field" :class="{ invalid: validationErrors.start_time }">
                                <label class="form-label">From</label>
                                <b-form-input
                                  v-model="form.start_time"
                                  type="time"
                                  :class="{ 'is-invalid': validationErrors.start_time }"
                                ></b-form-input>
                                <small v-if="validationErrors.start_time" class="text-danger">{{ validationErrors.start_time }}</small>
                              </div>
                              <div class="time-range-field" :class="{ invalid: validationErrors.end_time }">
                                <label class="form-label">To</label>
                                <b-form-input
                                  v-model="form.end_time"
                                  type="time"
                                  :min="oneOffMinimumEndTime"
                                  :class="{ 'is-invalid': validationErrors.end_time }"
                                ></b-form-input>
                                <small v-if="validationErrors.end_time" class="text-danger">{{ validationErrors.end_time }}</small>
                              </div>
                            </div>

                            <div v-if="oneOffTimeRangeError" class="text-danger small one-off-range-error">
                              {{ oneOffTimeRangeError }}
                            </div>

                            <div class="one-off-breaks-block">
                              <div class="d-flex justify-content-between align-items-center mb-2">
                                <div>
                                  <h6 class="mb-1">Break Windows</h6>
                                  <p class="text-muted mb-0 small">Add optional break windows that stay inside the selected time range.</p>
                                </div>
                                <b-button
                                  v-if="canAddOneOffBreaks"
                                  type="button"
                                  size="sm"
                                  variant="outline-primary"
                                  @click="addBreak"
                                  :disabled="form.breaks.length >= settings.max_breaks_per_availability"
                                >
                                  <i class="mdi mdi-plus-circle-outline me-1"></i>
                                  Add Break
                                </b-button>
                              </div>

                              <div v-if="!canAddOneOffBreaks" class="empty-breaks">
                                Select the availability date and full time range first before adding breaks.
                              </div>

                              <div v-else-if="!form.breaks.length" class="empty-breaks">
                                No break windows added yet.
                              </div>

                              <div v-for="(item, index) in form.breaks" :key="index" class="break-card">
                                <div class="break-card-head">
                                  <div>
                                    <strong class="break-title">Break {{ index + 1 }}</strong>
                                    <div class="small text-muted">This break will stay inside the selected one-time window.</div>
                                  </div>
                                  <button type="button" class="break-remove-btn" @click="removeBreak(index)" aria-label="Remove break">
                                    <i class="mdi mdi-close-circle-outline" aria-hidden="true"></i>
                                  </button>
                                </div>
                                <div class="row align-items-end">
                                  <div class="col-md-6 mb-2 mb-md-0">
                                    <label class="form-label">Break Start</label>
                                    <b-form-input
                                      v-model="item.break_start_time"
                                      type="time"
                                      :min="form.start_time || ''"
                                      :max="oneOffBreakStartMax"
                                      @input="clampOneOffBreak(index)"
                                    ></b-form-input>
                                  </div>
                                  <div class="col-md-6 mb-2 mb-md-0">
                                    <label class="form-label">Break End</label>
                                    <b-form-input :value="computedBreakEnd(item.break_start_time)" type="text" disabled></b-form-input>
                                  </div>
                                </div>
                                <div
                                  class="break-feedback mt-2"
                                  :class="{
                                    'break-feedback--empty': oneOffBreakState(item.break_start_time) === 'empty',
                                    'break-feedback--invalid': oneOffBreakState(item.break_start_time) === 'invalid',
                                    'break-feedback--valid': oneOffBreakState(item.break_start_time) === 'valid'
                                  }"
                                >
                                  <span v-if="oneOffBreakState(item.break_start_time) === 'empty'">
                                    Choose a break time inside the selected one-time range.
                                  </span>
                                  <span v-else-if="oneOffBreakState(item.break_start_time) === 'invalid'">
                                    This break must fit inside the selected one-time time range.
                                  </span>
                                  <span v-else class="break-feedback-success">
                                    <i class="mdi mdi-check-decagram break-feedback-success-icon" aria-hidden="true"></i>
                                    Break time fits inside the selected one-time range.
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div v-else-if="form.type === 'recurring' && createWizardStep === 3" class="wizard-pane wizard-stage">
                        <div class="wizard-card wizard-card-medium recurring-range-card">
                          <div class="wizard-card-head">
                            <span class="wizard-card-kicker">Step 3</span>
                            <h6>Choose a Time Range</h6>
                            <p>Set the start and end dates first. The weekly sheet comes next.</p>
                          </div>
                          <div class="time-range-grid two-up">
                            <div class="time-range-field" :class="{ invalid: validationErrors.start_date }">
                              <label class="form-label">Availability Date From</label>
                              <b-form-input v-model="recurringSheet.start_date" type="date" :min="todayIso" :class="{ 'is-invalid': validationErrors.start_date }"></b-form-input>
                              <small v-if="validationErrors.start_date" class="text-danger">{{ validationErrors.start_date }}</small>
                            </div>
                            <div class="time-range-field" :class="{ invalid: validationErrors.end_date }">
                              <label class="form-label">Availability Date To</label>
                              <b-form-input v-model="recurringSheet.end_date" type="date" :min="recurringMinimumEndDate" :class="{ 'is-invalid': validationErrors.end_date }"></b-form-input>
                              <small v-if="validationErrors.end_date" class="text-danger">{{ validationErrors.end_date }}</small>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div v-else-if="form.type === 'recurring' && createWizardStep === 4" class="wizard-pane recurring-sheet-pane">
                        <div class="wizard-card wizard-card-sheet">
                          <div class="wizard-card-head">
                            <span class="wizard-card-kicker">Step 4</span>
                            <h6>Select the Availability</h6>
                            <p>Paint the weekly sheet carefully. Green is availability, orange is break, red is already occupied.</p>
                          </div>
                          <div v-if="showBreakGuidancePanel" class="break-guidance-panel">
                            <div class="break-guidance-copy">
                              <span class="break-guidance-eyebrow">Break guidance</span>
                              <h6 class="mb-1">Add the hospital break for each selected day</h6>
                              <p class="mb-0">
                                Your availability is already marked in green. Now tap <strong>Create Breaks</strong>, choose the quarter where the break should start,
                                and the system will place the hospital break duration for you.
                              </p>
                            </div>
                            <div class="break-guidance-meta">
                              <span class="break-guidance-chip">Duration: {{ settings.default_break_duration_minutes }} minutes</span>
                              <span class="break-guidance-chip">Days waiting: {{ recurringDaysMissingBreaks.join(", ") }}</span>
                            </div>
                          </div>

                          <div class="sheet-legend">
                            <span><i class="legend-box free"></i> Free</span>
                            <span><i class="legend-box occupied"></i> Already taken</span>
                            <span><i class="legend-box availability"></i> Availability</span>
                            <span><i class="legend-box break"></i> Break</span>
                            <b-button
                              type="button"
                              size="sm"
                              class="ms-auto break-mode-toggle"
                              :class="{ 'needs-attention': shouldPromptForBreaks }"
                              :variant="isBreakMode ? 'warning' : 'outline-warning'"
                              @click="toggleBreakMode"
                            >
                              {{ isBreakMode ? "Done Creating Breaks" : "Create Breaks" }}
                            </b-button>
                            <span class="small text-muted">{{ breakModeHint }}</span>
                          </div>
                          <small v-if="breakGuidanceMessage" class="text-warning d-block mt-2">
                            {{ breakGuidanceMessage }}
                          </small>

                          <div class="recurring-sheet-scroll">
                            <table class="recurring-sheet-table">
                              <tbody>
                                <tr v-for="day in recurringDays" :key="day.key" :class="dayRowClass(day.key)">
                                  <th :class="dayHeaderClass(day.key)">
                                    <div class="day-header-top">
                                      <span>{{ day.label }}</span>
                                      <div v-if="shouldShowDayBreakActions(day.key)" class="day-break-actions">
                                        <b-button
                                          v-if="dayNeedsBreak(day.key) && !(isBreakMode && focusedBreakDay === day.key)"
                                          type="button"
                                          size="sm"
                                          variant="warning"
                                          class="day-break-action-btn"
                                          @click="activateBreakGuidanceForDay(day.key)"
                                        >
                                          Create Breaks
                                        </b-button>
                                        <b-button
                                          v-if="isBreakMode && focusedBreakDay === day.key"
                                          type="button"
                                          size="sm"
                                          variant="outline-warning"
                                          class="day-break-action-btn"
                                          @click="finishBreakGuidanceForDay(day.key)"
                                        >
                                          Done
                                        </b-button>
                                      </div>
                                    </div>
                                    <small :class="dayBreakUsageClass(day.key)">{{ dayBreakUsage(day.key) }}</small>
                                    <small v-if="dayNeedsBreak(day.key)" class="day-break-warning">
                                      Add at least one break.
                                    </small>
                                  </th>
                                  <td v-for="column in hourlyColumns" :key="`${day.key}-${column.key}`">
                                    <div class="sheet-cell-hour">
                                      <template v-if="shouldRenderQuarterSegments(day.key, column)">
                                        <button
                                          v-for="quarter in quarterSegments"
                                          :key="`${day.key}-${column.key}-${quarter.index}`"
                                          type="button"
                                          class="sheet-quarter"
                                          :class="sheetQuarterClass(day.key, column, quarter.index)"
                                          :title="sheetQuarterTitle(day.key, column, quarter.index)"
                                          @mouseenter="scheduleBreakHover(day.key, column.start, quarter.index, 'quarter')"
                                          @mouseleave="clearBreakHover"
                                          @mousedown.prevent="startAvailabilityPaint(day.key, column, quarter.index)"
                                          @mouseenter.prevent="extendAvailabilityPaint(day.key, column, quarter.index)"
                                          @mouseup.prevent="finishAvailabilityPaint(day.key, column, quarter.index)"
                                        >
                                          <span
                                            v-if="isBreakHoverTarget(day.key, column.start, quarter.index, 'quarter')"
                                            class="sheet-break-cta"
                                          >
                                            Create Break
                                          </span>
                                          <span v-else class="visually-hidden">{{ day.label }} {{ column.label }} {{ quarter.label }}</span>
                                        </button>
                                      </template>
                                      <button
                                        v-else
                                        type="button"
                                        class="sheet-hour"
                                        :class="sheetHourClass(day.key, column)"
                                        :title="sheetHourTitle(day.key, column)"
                                        @mouseenter="scheduleBreakHover(day.key, column.start, 0, 'hour')"
                                        @mouseleave="clearBreakHover"
                                        @mousedown.prevent="startHourAvailabilityPaint(day.key, column)"
                                        @mouseenter.prevent="extendHourAvailabilityPaint(day.key, column)"
                                        @mouseup.prevent="finishHourAvailabilityPaint(day.key, column)"
                                      >
                                        <span
                                          v-if="isBreakHoverTarget(day.key, column.start, 0, 'hour')"
                                          class="sheet-break-cta"
                                        >
                                          Create Break
                                        </span>
                                        <span v-else class="sheet-hour-label">{{ formatTime(column.start) }}</span>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>

                      <div v-else class="wizard-pane wizard-stage">
                        <div class="wizard-card wizard-card-medium">
                          <div class="wizard-card-head">
                            <span class="wizard-card-kicker">Step 5</span>
                            <h6>Review Before Submit</h6>
                            <p>Check the final submission details below. This is the exact availability setup that will be sent.</p>
                          </div>

                          <div class="review-hero">
                            <div>
                              <span class="review-hero-kicker">Submission Preview</span>
                              <h5 class="mb-1">{{ selectedService?.service_name || "Availability" }}</h5>
                              <p class="mb-0">
                                {{ form.type === "recurring" ? "Recurring availability pattern ready for review." : "One-time availability window ready for review." }}
                              </p>
                            </div>
                            <div class="review-hero-actions">
                              <span class="review-hero-badge">Ready to submit</span>
                              <b-button type="button" variant="primary" class="review-submit-btn" @click="openSubmitAvailabilityModal">
                                <i class="mdi mdi-check-circle-outline me-1"></i>
                                Submit Availability
                              </b-button>
                            </div>
                          </div>

                          <div class="review-layout">
                            <div class="review-main">
                              <section class="review-panel">
                                <div class="review-panel-head">
                                  <h6>Availability Window</h6>
                                  <p>{{ form.type === "recurring" ? "Review the schedule range and recurring setup." : "Review the exact day and time window." }}</p>
                                </div>

                                <div v-if="form.type === 'recurring'" class="review-detail-list">
                                  <div class="review-detail-row">
                                    <span class="review-detail-label">Availability Type</span>
                                    <strong class="review-detail-value">Recurring</strong>
                                  </div>
                                  <div class="review-detail-row">
                                    <span class="review-detail-label">Date Range</span>
                                    <strong class="review-detail-value">{{ recurringDateRangeLabel }}</strong>
                                  </div>
                                  <div class="review-detail-row">
                                    <span class="review-detail-label">Selected Days</span>
                                    <strong class="review-detail-value">{{ recurringSelectedDayLabels || "None selected" }}</strong>
                                  </div>
                                  <div class="review-detail-row">
                                    <span class="review-detail-label">Total Blocks</span>
                                    <strong class="review-detail-value">{{ recurringSelectionCount }}</strong>
                                  </div>
                                </div>

                                <div v-else class="review-detail-list">
                                  <div class="review-detail-row">
                                    <span class="review-detail-label">Availability Type</span>
                                    <strong class="review-detail-value">One Time</strong>
                                  </div>
                                  <div class="review-detail-row">
                                    <span class="review-detail-label">Availability Date</span>
                                    <strong class="review-detail-value">{{ form.specific_date ? formatDate(form.specific_date) : "Not set" }}</strong>
                                  </div>
                                  <div class="review-detail-row">
                                    <span class="review-detail-label">Time Window</span>
                                    <strong class="review-detail-value">{{ form.start_time && form.end_time ? formatTimeRange(form.start_time, form.end_time) : "Not set" }}</strong>
                                  </div>
                                  <div class="review-detail-row">
                                    <span class="review-detail-label">Break Windows</span>
                                    <strong class="review-detail-value">{{ oneOffBreakPreview.length || 0 }}</strong>
                                  </div>
                                </div>
                              </section>

                              <section v-if="form.type === 'recurring'" class="review-panel">
                                <div class="review-panel-head">
                                  <h6>Weekly Pattern</h6>
                                  <p>Grouped view of the recurring availability and break windows for each selected day.</p>
                                </div>

                                <div v-if="weeklyPatternSummary.length" class="review-pattern-list">
                                  <div v-for="(entry, index) in weeklyPatternSummary" :key="index" class="review-pattern-card">
                                    <strong class="review-pattern-title">{{ entry.days }}</strong>
                                    <div v-for="(line, lineIndex) in entry.lines" :key="lineIndex" class="review-pattern-line">
                                      {{ line }}
                                    </div>
                                  </div>
                                </div>
                                <div v-else class="review-empty-state">
                                  No recurring pattern has been selected yet.
                                </div>
                              </section>

                              <section v-else class="review-panel">
                                <div class="review-panel-head">
                                  <h6>Break Windows</h6>
                                  <p>These are the time windows that will remain unavailable inside the one-time slot.</p>
                                </div>

                                <div v-if="oneOffBreakPreview.length" class="review-break-list">
                                  <div v-for="(breakItem, index) in oneOffBreakPreview" :key="`oneoff-break-${index}`" class="review-break-item">
                                    <span class="review-break-index">Break {{ index + 1 }}</span>
                                    <strong>{{ formatTimeRange(breakItem.break_start_time, breakItem.break_end_time) }}</strong>
                                  </div>
                                </div>
                                <div v-else class="review-empty-state">
                                  No valid break windows were added.
                                </div>
                              </section>

                            </div>

                            <div class="review-side">
                              <section class="review-panel review-panel--accent">
                                <div class="review-panel-head">
                                  <h6>Submission Check</h6>
                                  <p>Quick confirmation before you submit this availability.</p>
                                </div>

                                <div class="review-check-list">
                                  <div class="review-check-item">
                                    <i class="mdi mdi-check-circle"></i>
                                    <span>Bookable service selected</span>
                                  </div>
                                  <div class="review-check-item">
                                    <i class="mdi mdi-check-circle"></i>
                                    <span>{{ form.type === "recurring" ? "Recurring pattern completed" : "One-time range and breaks completed" }}</span>
                                  </div>
                                  <div class="review-check-item">
                                    <i class="mdi mdi-check-circle"></i>
                                    <span>Ready for final submission</span>
                                  </div>
                                </div>

                                <div class="review-notes-hint mt-3">
                                  {{ form.notes ? "Optional submission notes have been added." : "You can add optional notes in the submit popup." }}
                                </div>
                              </section>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </transition>
                </div>

                <aside v-if="showWizardSummaryCard" class="wizard-summary-card">
                  <div class="wizard-summary-head">
                    <span class="wizard-summary-kicker">Availability Summary</span>
                    <h6>{{ currentWizardStepMeta.title }}</h6>
                    <p>We’ll keep this updated as you work through the setup.</p>
                  </div>

                  <div class="wizard-summary-section">
                    <div v-if="selectedService" class="wizard-summary-row">
                      <span class="wizard-summary-label">Selected service</span>
                      <strong class="wizard-summary-value">{{ selectedService ? selectedService.service_name : "Not selected" }}</strong>
                      <small v-if="selectedService?.service_category_name" class="wizard-summary-meta">{{ selectedService.service_category_name }}</small>
                    </div>

                    <div v-if="showAvailabilityTypeSummary" class="wizard-summary-row">
                      <span class="wizard-summary-label">Availability type</span>
                      <strong class="wizard-summary-value">{{ availabilityTypeSummary }}</strong>
                    </div>

                    <div v-if="showScheduleRangeSummary" class="wizard-summary-row">
                      <span class="wizard-summary-label">{{ scheduleRangeSummaryLabel }}</span>
                      <strong class="wizard-summary-value">{{ scheduleRangeSummary }}</strong>
                    </div>

                    <div v-if="showScheduleTimeSummary" class="wizard-summary-row">
                      <span class="wizard-summary-label">{{ scheduleTimeSummaryLabel }}</span>
                      <strong class="wizard-summary-value">{{ scheduleTimeSummary }}</strong>
                    </div>
                  </div>

                  <div v-if="showSummaryStats" class="wizard-summary-divider"></div>

                  <div v-if="showSummaryStats" class="wizard-summary-section">
                    <div class="wizard-summary-stat-grid">
                      <div class="wizard-summary-stat">
                        <span class="wizard-summary-stat-label">{{ summaryMetricOneLabel }}</span>
                        <strong class="wizard-summary-stat-value">{{ summaryMetricOneValue }}</strong>
                      </div>
                      <div class="wizard-summary-stat">
                        <span class="wizard-summary-stat-label">{{ summaryMetricTwoLabel }}</span>
                        <strong class="wizard-summary-stat-value">{{ summaryMetricTwoValue }}</strong>
                      </div>
                    </div>
                  </div>

                  <div v-if="wizardSummaryNote" class="wizard-summary-note">
                    {{ wizardSummaryNote }}
                  </div>
                </aside>
              </div>

              <div class="wizard-footer">
                <b-button type="button" variant="light" @click="closeCreateAvailabilityModal">Cancel</b-button>
                <div class="wizard-footer-actions">
                  <b-button v-if="createWizardStep > 1" type="button" variant="outline-secondary" @click="previousCreateStep">Back</b-button>
                  <b-button type="submit" variant="primary" :disabled="isCreatePrimaryDisabled">
                    <b-spinner v-if="submitting && isFinalCreateStep" small class="align-middle me-1"></b-spinner>
                    {{ createPrimaryButtonLabel }}
                  </b-button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      <div v-else class="row">
        <div class="col-12">
          <b-tabs v-model="activeDoctorCenterTab" pills nav-class="p-0 doctor-center-tabs mb-3" content-class="text-dark">
            <b-tab active class="border-0">
              <template #title>My Slot Calendar</template>
              <div class="card calendar-shell-card">
                <div class="card-body">
                  <div class="section-head mb-3">
                    <div>
                      <h4 class="card-title mb-1">My Slot Calendar</h4>
                      <p class="text-muted mb-0">Select a touched day and inspect its slots without stretching the page.</p>
                    </div>
                    <div class="calendar-controls">
                      <b-button size="sm" variant="light" @click="shiftMonth(-1)">
                        <i class="mdi mdi-chevron-left"></i>
                      </b-button>
                      <span class="fw-medium">{{ calendarLabel }}</span>
                      <b-button size="sm" variant="light" @click="shiftMonth(1)">
                        <i class="mdi mdi-chevron-right"></i>
                      </b-button>
                    </div>
                  </div>

                  <div class="calendar-shell">
                    <div class="calendar-pane">
                      <div class="calendar-grid">
                        <div v-for="weekday in weekDays" :key="weekday" class="calendar-head">{{ weekday }}</div>
                        <button
                          v-for="day in calendarCells"
                          :key="day.key"
                          type="button"
                          class="calendar-cell"
                          :class="{ muted: !day.inMonth, active: selectedCalendarDate === day.date, today: day.date === todayIso, touched: day.count > 0, booked: day.booked > 0 }"
                          @click="selectCalendarDate(day.date)"
                        >
                          <span class="day-number">{{ day.day }}</span>
                          <span v-if="day.count" class="calendar-count">{{ day.count }}</span>
                          <span v-if="day.booked" class="calendar-booked-dot"></span>
                        </button>
                      </div>
                    </div>

                    <div class="slots-pane">
                      <div class="slots-pane-head">
                        <div>
                          <h5 class="mb-1">Day Schedule</h5>
                          <small class="text-muted">{{ selectedCalendarDateLabel }}</small>
                        </div>
                        <span v-if="selectedDayBookedCount" class="badge bg-soft-danger text-danger">
                          {{ selectedDayBookedCount }} booked
                        </span>
                      </div>

                      <div v-if="loadingDaySlots" class="slots-state">
                        <b-spinner small class="align-middle me-1"></b-spinner>
                        <strong>Loading your slots...</strong>
                      </div>

                      <div v-else-if="!daySlots.length" class="slots-state empty">
                        No slots are available for this date yet.
                      </div>

                      <div v-else class="slot-list">
                        <div
                          v-for="slot in daySlots"
                          :id="slotPopoverTarget(slot)"
                          :key="slot.id"
                          class="slot-card"
                          :class="{ 'slot-card--booked': slot.status === 'booked' }"
                        >
                          <div class="d-flex justify-content-between align-items-start gap-2">
                            <div>
                              <strong>{{ slot.service_name }}</strong>
                              <div class="small text-muted">{{ formatTimeRange(slot.start_time, slot.end_time) }}</div>
                            </div>
                            <span class="badge" :class="slot.status === 'booked' ? 'bg-soft-danger text-danger' : 'bg-soft-success text-success'">
                              {{ formatStatus(slot.status) }}
                            </span>
                          </div>
                          <div v-if="slot.status === 'booked'" class="slot-card-foot mt-2">
                            <span class="slot-booking-pill">
                              <i class="mdi mdi-account-clock-outline me-1"></i>
                              Hover to view booking details
                            </span>
                          </div>
                        </div>

                        <b-popover
                          v-for="slot in bookedDaySlots"
                          :key="`slot-popover-${slot.id}`"
                          :target="slotPopoverTarget(slot)"
                          triggers="hover focus"
                          placement="auto"
                          boundary="viewport"
                          custom-class="slot-booking-popover"
                        >
                          <div class="slot-booking-popover-body">
                            <div class="slot-booking-popover-head">
                              <span class="slot-booking-popover-kicker">Booked Slot</span>
                              <strong>{{ formatTimeRange(slot.start_time, slot.end_time) }}</strong>
                            </div>
                            <div class="slot-booking-popover-service">{{ slot.service_name }}</div>

                            <div v-if="slotBookingDetails(slot).length" class="slot-booking-popover-grid">
                              <div v-for="detail in slotBookingDetails(slot)" :key="detail.label" class="slot-booking-popover-row">
                                <span class="slot-booking-popover-label">{{ detail.label }}</span>
                                <strong class="slot-booking-popover-value">{{ detail.value }}</strong>
                              </div>
                            </div>

                            <div v-else class="slot-booking-popover-empty">
                              Booking details are not available for this slot yet.
                            </div>
                          </div>
                        </b-popover>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </b-tab>

            <b-tab class="border-0">
              <template #title>My Availability</template>
              <div class="card data-table-card">
                <div class="card-body">
                  <div class="section-head table-head mb-3">
                    <div>
                      <h4 class="card-title mb-1">My Availability</h4>
                      <p class="text-muted mb-0">Track submitted availability, status, and slot creation without the layout breaking as data grows.</p>
                    </div>
                    <div class="table-tools">
                      <b-form-input
                        v-model="searchTerm"
                        placeholder="Search service, type, status, note..."
                        @input="queueSearch"
                      ></b-form-input>
                    </div>
                  </div>

                  <div class="table-responsive">
                    <b-table :items="availabilities" :fields="availabilityFields" :busy="loading" responsive="md" show-empty empty-text="No availability submitted yet.">
                      <template #table-busy>
                        <div class="text-center text-primary my-3">
                          <b-spinner class="align-middle me-1" small></b-spinner>
                          <strong>Loading your availability...</strong>
                        </div>
                      </template>

                      <template #cell(service_name)="data">
                        <div>
                          <strong>{{ data.item.service_name }}</strong>
                          <div class="small text-muted">{{ data.item.service_category_name || "No category" }}</div>
                        </div>
                      </template>

                      <template #cell(schedule)="data">
                        <div class="schedule-cell">
                          <strong>{{ scheduleLabel(data.item) }}</strong>
                          <div class="small text-muted">{{ scheduleWindowLabel(data.item) }}</div>
                        </div>
                      </template>

                      <template #cell(status)="data">
                        <span class="badge" :class="statusBadgeClass(data.item.status)">{{ formatStatus(data.item.status) }}</span>
                        <div v-if="data.item.rejection_reason" class="small text-danger mt-1">{{ data.item.rejection_reason }}</div>
                      </template>

                      <template #cell(submitted_on)="data">
                        <div>{{ formatDateTime(data.item.created_at) }}</div>
                      </template>

                      <template #cell(actions)="data">
                        <b-button v-if="data.item.status === 'pending_review'" size="sm" variant="danger" :disabled="withdrawingId === data.item.id" @click="confirmWithdrawAvailability(data.item)">
                          <b-spinner v-if="withdrawingId === data.item.id" small class="align-middle me-1"></b-spinner>
                          {{ withdrawingId === data.item.id ? "Withdrawing..." : "Withdraw" }}
                        </b-button>
                      </template>
                    </b-table>
                  </div>

                  <div class="table-footer">
                    <div class="small text-muted">
                      Showing {{ paginationLabel }}
                    </div>
                    <b-pagination
                      v-model="pagination.current_page"
                      :total-rows="pagination.total"
                      :per-page="pagination.per_page"
                      pills
                      @input="changePage"
                    ></b-pagination>
                  </div>
                </div>
              </div>
            </b-tab>
          </b-tabs>
        </div>
      </div>
    </template>

    <b-modal id="availability-preview-modal" centered hide-footer title="Availability Check">
      <div v-if="previewResult.ok" class="alert alert-success mb-3">{{ previewResult.message }}</div>
      <div v-else class="alert alert-danger mb-3">{{ previewResult.message }}</div>

      <div v-if="previewResult.breaks?.length" class="mb-3">
        <h6 class="mb-2">Break Windows</h6>
        <div class="small text-muted" v-for="(item, index) in previewResult.breaks" :key="index">
          {{ formatTimeRange(item.break_start_time, item.break_end_time) }}
        </div>
      </div>

      <div v-if="previewResult.conflicts?.length">
        <h6 class="mb-2">Detected Conflicts</h6>
        <ul class="mb-0 ps-3">
          <li v-for="(item, index) in previewResult.conflicts" :key="`${item.id || 'new'}-${item.batch_index ?? 'x'}-${index}`">
            {{ item.batch_index !== undefined ? `Selection ${item.batch_index + 1} | ` : "" }}{{ item.service_name || "Existing availability" }} | {{ formatTimeRange(item.start_time, item.end_time) }}
          </li>
        </ul>
      </div>

      <div class="d-flex justify-content-end mt-3">
        <b-button type="button" variant="secondary" @click="$bvModal.hide('availability-preview-modal')">Close</b-button>
      </div>
    </b-modal>

    <b-modal v-model="showSheetCellActionModal" centered hide-footer title="Set Hour Segment">
      <div v-if="activeSheetCell">
        <div class="small text-muted mb-3">{{ activeSheetCell.label }}</div>
        <div class="mb-3">
          <label class="form-label">Break quarter</label>
          <b-form-select v-model="activeBreakQuarter" :options="breakQuarterOptions"></b-form-select>
        </div>
        <div class="d-grid gap-2">
          <b-button v-if="!isBreakMode" type="button" variant="success" @click="applyAvailabilityFromActionCell">Mark as Availability</b-button>
          <b-button type="button" variant="warning" :disabled="breakSelectionDisabled" @click="applyBreakFromActionCell">
            {{ isBreakMode ? "Place Break Here" : "Mark as Break Time" }}
          </b-button>
          <b-button v-if="actionCellHasSelection" type="button" variant="outline-secondary" @click="clearActionCellSelection">
            Clear Selection
          </b-button>
        </div>
      </div>
    </b-modal>

    <b-modal v-model="showWithdrawConfirmModal" centered hide-footer title="Withdraw Availability">
      <div v-if="pendingWithdrawAvailability">
        <p class="mb-2">Are you sure you want to withdraw this availability?</p>
        <div class="small text-muted mb-3">
          {{ pendingWithdrawAvailability.service_name }} | {{ scheduleWindowLabel(pendingWithdrawAvailability) }}
        </div>
        <div class="d-flex justify-content-end gap-2">
          <b-button type="button" variant="light" @click="cancelWithdrawConfirmation">Cancel</b-button>
          <b-button type="button" variant="danger" :disabled="withdrawingId === pendingWithdrawAvailability.id" @click="withdrawAvailability">
            <b-spinner v-if="withdrawingId === pendingWithdrawAvailability.id" small class="align-middle me-1"></b-spinner>
            {{ withdrawingId === pendingWithdrawAvailability.id ? "Withdrawing..." : "Yes, Withdraw" }}
          </b-button>
        </div>
      </div>
    </b-modal>

    <b-modal
      id="submit-availability-modal"
      centered
      hide-footer
      title="Submit Availability"
      modal-class="schedule-modal-shell"
    >
      <form @submit.prevent="confirmSubmitAvailability">
        <div class="mb-3">
          <p class="mb-2">Add any optional notes for the appointment manager before you submit.</p>
          <label class="form-label">Notes</label>
          <b-form-textarea
            v-model="form.notes"
            rows="4"
            placeholder="Optional notes for the appointment manager"
          ></b-form-textarea>
        </div>
        <div class="d-flex justify-content-end gap-2">
          <b-button type="button" variant="secondary" @click="$bvModal.hide('submit-availability-modal')">Cancel</b-button>
          <b-button type="submit" variant="primary" :disabled="submitting">
            <b-spinner v-if="submitting" small class="align-middle me-1"></b-spinner>
            {{ submitting ? "Submitting..." : "Submit Availability" }}
          </b-button>
        </div>
      </form>
    </b-modal>
  </Layout>
</template>

<script>
import axios from "axios";
import Multiselect from "vue-multiselect";
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";

export default {
  page: {
    title: "Doctor Center",
    meta: [{ name: "description", content: appConfig.description }],
  },
  components: { Layout, PageHeader, Multiselect },
  data() {
    return {
      title: "Doctor Center",
      items: [
        { text: "Dashboard", to: "/" },
        { text: "Doctor Center", active: true },
      ],
      userData: JSON.parse(localStorage.getItem("user")) || {},
      loading: false,
      loadingDaySlots: false,
      submitting: false,
      withdrawingId: null,
      pendingWithdrawAvailability: null,
      searchTimer: null,
      showCreateAvailabilityModal: false,
      showSheetCellActionModal: false,
      showWithdrawConfirmModal: false,
      activeDoctorCenterTab: 0,
      createWizardStep: 1,
      activeSheetCell: null,
      activeBreakQuarter: 0,
      isBreakMode: false,
      focusedBreakDay: "",
      blockingAvailabilities: [],
      isSheetPointerDown: false,
      isDraggingAvailability: false,
      dragOriginKey: "",
      dragVisitedKeys: [],
      breakHoverTimer: null,
      breakHoverTarget: null,
      services: [],
      selectedService: null,
      availabilities: [],
      calendarDays: [],
      daySlots: [],
      daySlotBookings: [],
      selectedCalendarDate: "",
      currentMonth: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      searchTerm: "",
      pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
      },
      previewResult: {
        ok: true,
        message: "",
        breaks: [],
        conflicts: [],
      },
      validationErrors: {},
      settings: {
        max_breaks_per_availability: 2,
        default_break_duration_minutes: 15,
      },
      form: {
        type: "",
        specific_date: "",
        start_time: "",
        end_time: "",
        breaks: [],
        notes: "",
      },
      recurringSheet: {
        start_date: "",
        end_date: "",
        selections: {},
      },
      availabilityFields: [
        { key: "service_name", label: "Service" },
        { key: "schedule", label: "Availability Window" },
        { key: "status", label: "Status" },
        { key: "slots_count", label: "Slots" },
        { key: "submitted_on", label: "Submitted" },
        { key: "actions", label: "Actions" },
      ],
      recurringDays: [
        { key: "monday", label: "Monday" },
        { key: "tuesday", label: "Tuesday" },
        { key: "wednesday", label: "Wednesday" },
        { key: "thursday", label: "Thursday" },
        { key: "friday", label: "Friday" },
        { key: "saturday", label: "Saturday" },
        { key: "sunday", label: "Sunday" },
      ],
      weekDays: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
    };
  },
  computed: {
    canUseDoctorCenter() {
      const permissions = this.userData?.permissions || {};
      return [
        permissions.view_doctor,
        permissions.consultation_patients,
        permissions.procedure_patients,
        permissions.dental_patients,
        permissions.radiology_patients,
        permissions.antenatal_patients,
        permissions.other_patients,
        permissions.view_psychology,
      ].some((value) => Number(value || 0) === 1);
    },
    isCreateAvailabilityPage() {
      return this.$route.path === "/appointments/doctor-center/create-availability";
    },
    calendarMap() {
      return this.calendarDays.reduce((acc, item) => {
        acc[item.slot_date] = item;
        return acc;
      }, {});
    },
    calendarLabel() {
      return this.currentMonth.toLocaleString(undefined, { month: "long", year: "numeric" });
    },
    calendarCells() {
      const start = new Date(this.currentMonth);
      const firstDay = start.getDay();
      start.setDate(1 - firstDay);
      const cells = [];
      for (let index = 0; index < 42; index += 1) {
        const date = new Date(start);
        date.setDate(start.getDate() + index);
        const iso = this.isoDate(date);
        const daySummary = this.calendarMap[iso] || {};
        cells.push({
          key: `${iso}-${index}`,
          date: iso,
          day: date.getDate(),
          inMonth: date.getMonth() === this.currentMonth.getMonth(),
          count: Number(daySummary.total_slots || 0),
          booked: Number(daySummary.booked_slots || 0),
        });
      }
      return cells;
    },
    selectedCalendarDateLabel() {
      if (!this.selectedCalendarDate) {
        return "Choose a calendar day";
      }
      return this.formatDate(this.selectedCalendarDate);
    },
    selectedDayBookedCount() {
      return this.daySlots.filter((slot) => slot.status === "booked").length;
    },
    bookedDaySlots() {
      return this.daySlots.filter((slot) => slot.status === "booked");
    },
    daySlotBookingsBySlotId() {
      return this.daySlotBookings.reduce((acc, booking) => {
        if (booking.appointment_slot_id) {
          acc[booking.appointment_slot_id] = booking;
        }
        return acc;
      }, {});
    },
    recurringDateRangeLabel() {
      if (!this.recurringSheet.start_date) {
        return "Not set";
      }
      if (!this.recurringSheet.end_date) {
        return this.formatDate(this.recurringSheet.start_date);
      }
      return `${this.formatDate(this.recurringSheet.start_date)} to ${this.formatDate(this.recurringSheet.end_date)}`;
    },
    todayIso() {
      return this.isoDate(new Date());
    },
    oneOffMinimumEndTime() {
      if (!this.form.start_time) {
        return "";
      }
      return this.minutesToTime(Math.min(this.timeToMinutes(this.form.start_time) + 30, (23 * 60) + 59));
    },
    oneOffTimeRangeError() {
      if (!this.form.start_time || !this.form.end_time) {
        return "";
      }
      if (this.timeToMinutes(this.form.end_time) <= this.timeToMinutes(this.form.start_time)) {
        return "The 'To' time must be later than the 'From' time.";
      }
      if ((this.timeToMinutes(this.form.end_time) - this.timeToMinutes(this.form.start_time)) < 60) {
        return "The time range must be at least 1 hour.";
      }
      return "";
    },
    recurringMinimumEndDate() {
      return this.recurringSheet.start_date || this.todayIso;
    },
    paginationLabel() {
      if (!this.pagination.total) {
        return "0 records";
      }
      const start = ((this.pagination.current_page - 1) * this.pagination.per_page) + 1;
      const end = Math.min(this.pagination.current_page * this.pagination.per_page, this.pagination.total);
      return `${start}-${end} of ${this.pagination.total} records`;
    },
    activeWizardSteps() {
      if (this.form.type === "one_off") {
        return [
          { number: 1, short: "Service", title: "Choose a Service", caption: "Pick the bookable service first." },
          { number: 2, short: "Type", title: "Choose Availability Type", caption: "Select one-time or recurring availability." },
          {
            number: 3,
            short: "Range",
            title: "Choose Date, Time, and Breaks",
            caption: "Set the date, time window, and any optional break windows for this availability.",
          },
          { number: 4, short: "Review", title: "Preview and Submit", caption: "Review the availability, add notes, and submit." },
        ];
      }
      return [
        { number: 1, short: "Service", title: "Choose a Service", caption: "Pick the bookable service first." },
        { number: 2, short: "Type", title: "Choose Availability Type", caption: "Select one-time or recurring availability." },
        {
          number: 3,
          short: "Range",
          title: "Choose a Time Range",
          caption: this.form.type === "recurring"
            ? "Set the date range before opening the weekly sheet."
            : "Set the date and time window for this availability.",
        },
        {
          number: 4,
          short: this.form.type === "recurring" ? "Sheet" : "Breaks",
          title: this.form.type === "recurring" ? "Select the Availability" : "Add Break Windows",
          caption: this.form.type === "recurring"
            ? "Paint the weekly sheet and place breaks where needed."
            : "Add optional breaks inside the one-time window.",
        },
        { number: 5, short: "Review", title: "Preview and Submit", caption: "Review the availability, add notes, and submit." },
      ];
    },
    finalCreateStep() {
      return this.form.type === "one_off" ? 4 : 5;
    },
    currentWizardStepMeta() {
      return this.activeWizardSteps.find((step) => step.number === this.createWizardStep) || { title: "Create Availability", caption: "Follow the guided steps." };
    },
    wizardProgressWidth() {
      return `${Math.max((this.createWizardStep / this.finalCreateStep) * 100, 8)}%`;
    },
    isFinalCreateStep() {
      return this.createWizardStep === this.finalCreateStep;
    },
    wizardPaneKey() {
      return `${this.form.type || "unselected"}-${this.createWizardStep}`;
    },
    wizardProgressNodes() {
      return this.activeWizardSteps.map((step) => ({
        ...step,
        state: step.number < this.createWizardStep ? "complete" : step.number === this.createWizardStep ? "active" : "upcoming",
      }));
    },
    createPrimaryButtonLabel() {
      if (this.form.type === "recurring" && this.createWizardStep === 4 && this.shouldPromptForBreaks) {
        return "Create Breaks";
      }
      if (this.isFinalCreateStep) {
        return this.submitting ? "Submitting..." : "Submit Availability";
      }
      return "Continue";
    },
    showWizardSummaryCard() {
      if ((this.form.type === "recurring" && this.createWizardStep === 4) || this.isFinalCreateStep) {
        return false;
      }
      return [2, 3, 4, 5].includes(this.createWizardStep);
    },
    showAvailabilityTypeSummary() {
      return this.createWizardStep >= 3 && !!this.form.type;
    },
    showScheduleRangeSummary() {
      if (this.createWizardStep < 3) {
        return false;
      }
      if (this.form.type === "recurring") {
        return !!this.recurringSheet.start_date || !!this.recurringSheet.end_date;
      }
      return !!this.form.specific_date;
    },
    showScheduleTimeSummary() {
      if (this.createWizardStep < 3) {
        return false;
      }
      if (this.form.type === "recurring") {
        return this.createWizardStep >= 5 && this.recurringSelectionCount > 0;
      }
      return !!this.form.start_time || !!this.form.end_time;
    },
    showSummaryStats() {
      if (this.createWizardStep < 3) {
        return false;
      }
      if (this.form.type === "recurring") {
        return this.createWizardStep >= 5;
      }
      return true;
    },
    canAddOneOffBreaks() {
      return !!this.form.specific_date && !!this.form.start_time && !!this.form.end_time;
    },
    oneOffBreakStartMax() {
      if (!this.canAddOneOffBreaks) {
        return "";
      }
      return this.minutesToTime(
        Math.max(
          this.timeToMinutes(this.form.end_time) - Number(this.settings.default_break_duration_minutes || 0),
          this.timeToMinutes(this.form.start_time)
        )
      );
    },
    areOneOffBreaksValid() {
      return this.form.breaks.every((item) => !item.break_start_time || this.isBreakWithinOneOffWindow(item.break_start_time));
    },
    hasAtLeastOneValidOneOffBreak() {
      return this.form.breaks.some((item) => item.break_start_time && this.isBreakWithinOneOffWindow(item.break_start_time));
    },
    isCreatePrimaryDisabled() {
      if (this.submitting) {
        return true;
      }
      if (this.createWizardStep === 1) {
        return !this.selectedService?.id;
      }
      if (this.createWizardStep === 2) {
        return !this.form.type;
      }
      if (this.form.type === "one_off" && this.createWizardStep === 3) {
        return !this.form.specific_date
          || !this.form.start_time
          || !this.form.end_time
          || !!this.oneOffTimeRangeError
          || !this.areOneOffBreaksValid
          || !this.hasAtLeastOneValidOneOffBreak;
      }
      if (this.form.type === "recurring" && this.createWizardStep === 3) {
        return !this.recurringSheet.start_date || !this.recurringSheet.end_date;
      }
      if (this.form.type === "recurring" && this.createWizardStep === 4) {
        return !this.hasRecurringAvailabilitySelections();
      }
      return false;
    },
    availabilityTypeSummary() {
      if (this.form.type === "recurring") return "Recurring";
      if (this.form.type === "one_off") return "One Time";
      return "Not selected";
    },
    scheduleRangeSummaryLabel() {
      return this.form.type === "recurring" ? "Date range" : "Availability date";
    },
    scheduleRangeSummary() {
      if (this.form.type === "recurring") {
        if (!this.recurringSheet.start_date && !this.recurringSheet.end_date) {
          return "Not set";
        }
        if (!this.recurringSheet.start_date || !this.recurringSheet.end_date) {
          return "Incomplete";
        }
        return this.recurringDateRangeLabel;
      }
      if (this.form.specific_date) {
        return this.formatDate(this.form.specific_date);
      }
      return "Not set";
    },
    scheduleTimeSummaryLabel() {
      return this.form.type === "recurring" ? "Pattern time" : "Time window";
    },
    scheduleTimeSummary() {
      if (this.form.type === "recurring") {
        return this.recurringSelectionCount ? `${this.recurringSelectionCount} blocks selected` : "No blocks selected";
      }
      if (this.form.start_time && this.form.end_time) {
        return this.formatTimeRange(this.form.start_time, this.form.end_time);
      }
      if (this.form.start_time || this.form.end_time) {
        return "Incomplete";
      }
      return "Not set";
    },
    summaryMetricOneLabel() {
      return this.form.type === "recurring" ? "Selected days" : "Breaks";
    },
    summaryMetricOneValue() {
      if (this.form.type === "recurring") {
        return this.recurringSelectedDayLabels || "None";
      }
      return String(this.form.breaks.filter((item) => item.break_start_time).length);
    },
    summaryMetricTwoLabel() {
      return this.form.type === "recurring" ? "Break status" : "Readiness";
    },
    summaryMetricTwoValue() {
      if (this.form.type === "recurring") {
        return this.breakRulesAreConfigured
          ? (this.recurringDaysMissingBreaks.length ? `${this.recurringDaysMissingBreaks.length} day(s) pending` : "Complete")
          : "Optional";
      }
      if (this.form.start_time && this.form.end_time && this.form.specific_date) {
        return "Ready";
      }
      return "Pending";
    },
    oneOffBreakPreview() {
      return this.form.breaks
        .filter((item) => item.break_start_time && this.isBreakWithinOneOffWindow(item.break_start_time))
        .map((item) => ({
          break_start_time: item.break_start_time,
          break_end_time: this.computedBreakEnd(item.break_start_time),
        }));
    },
    wizardSummaryNote() {
      if (this.createWizardStep === 2) {
        return "Pick the availability type that matches how this service should be opened for booking.";
      }
      if (this.form.type === "recurring" && this.createWizardStep === 3) {
        return "Set the overall date range first. You will paint the weekly availability sheet in the next step.";
      }
      if (this.form.type === "one_off" && this.createWizardStep === 3) {
        return "Choose the exact day and time window, then add any optional breaks that must remain inside that window.";
      }
      if ((this.form.type === "one_off" && this.createWizardStep === 4) || (this.form.type === "recurring" && this.createWizardStep === 5)) {
        return "Review everything carefully before you submit. The summary reflects the selections made in the previous steps.";
      }
      return "";
    },
    hourlyColumns() {
      const orderedHours = [
        6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23,
        0, 1, 2, 3, 4,
      ];

      return orderedHours.map((hour) => {
        const start = `${String(hour).padStart(2, "0")}:00`;
        const endHour = (hour + 1) % 24;
        const end = `${String(endHour).padStart(2, "0")}:00`;
        return {
          key: start,
          start,
          end,
          label: `${this.formatTime(start)} - ${this.formatTime(end)}`,
        };
      });
    },
    quarterSegments() {
      return [
        { index: 0, label: ":00 - :15" },
        { index: 1, label: ":15 - :30" },
        { index: 2, label: ":30 - :45" },
        { index: 3, label: ":45 - :00" },
      ];
    },
    breakQuarterOptions() {
      if (!this.activeSheetCell) {
        return this.quarterSegments.map((quarter) => ({ value: quarter.index, text: quarter.label }));
      }

      return this.quarterSegments.map((quarter) => ({
        value: quarter.index,
        text: `${this.activeSheetCell.hourLabel} ${quarter.label}`,
      }));
    },
    occupiedRecurringCells() {
      const map = {};
      this.blockingAvailabilities.forEach((item) => {
        const targetDays = item.type === "recurring"
          ? [item.day_of_week]
          : [this.dayKeyFromDate(item.specific_date)];
        targetDays.filter(Boolean).forEach((day) => {
          this.hourlyColumns.forEach((column) => {
            this.quarterSegments.forEach((quarter) => {
              const quarterStart = this.quarterStart(column.start, quarter.index);
              const quarterEnd = this.minutesToTime(this.timeToMinutes(quarterStart) + 15);
              if (this.timeRangesOverlap(quarterStart, quarterEnd, item.start_time, item.end_time)) {
                map[this.segmentKey(day, column.start, quarter.index)] = item.type === "recurring"
                  ? "Recurring availability already exists"
                  : "One-time availability already exists";
              }
            });
          });
        });
      });
      return map;
    },
    recurringSelectedDayLabels() {
      const days = this.recurringDays
        .filter((day) => this.dayHasRecurringSelections(day.key))
        .map((day) => day.label);
      return days.join(", ");
    },
    recurringSelectionCount() {
      return this.recurringDays.reduce((count, day) => count + this.buildDaySegments(day.key).length, 0);
    },
    breakSelectionDisabled() {
      if (!this.activeSheetCell) {
        return true;
      }

      return !this.canPlaceBreakAt(this.activeSheetCell.day, this.activeSheetCell.hourStart, this.activeBreakQuarter).ok;
    },
    breakModeHint() {
      if (this.isBreakMode) {
        return "Break mode is on. Tap an hour, choose the quarter, and place the break.";
      }

      if (this.breakRulesAreConfigured) {
        return "Each recurring day with availability needs at least one break.";
      }

      return "Breaks per day depend on the selected row.";
    },
    actionCellHasSelection() {
      if (!this.activeSheetCell) {
        return false;
      }

      return this.quarterSegments.some((quarter) => {
        const state = this.sheetQuarterState(this.activeSheetCell.day, { start: this.activeSheetCell.hourStart }, quarter.index);
        return state === "availability" || state === "break";
      });
    },
    weeklyPatternSummary() {
      const grouped = {};

      this.recurringDays.forEach((day) => {
        const segments = this.buildDaySegments(day.key);
        if (!segments.length) {
          return;
        }

        const signature = JSON.stringify(segments);
        if (!grouped[signature]) {
          grouped[signature] = {
            days: [],
            segments,
          };
        }

        grouped[signature].days.push(day.label);
      });

      return Object.values(grouped).map((entry) => ({
        days: entry.days.join(", "),
        lines: entry.segments.map((segment) => `${this.titleCase(segment.type)}: ${this.formatTimeRange(segment.start_time, segment.end_time)}`),
      }));
    },
    breakRulesAreConfigured() {
      return Number(this.settings.max_breaks_per_availability || 0) > 0
        && Number(this.settings.default_break_duration_minutes || 0) > 0;
    },
    recurringDaysMissingBreaks() {
      return this.recurringDays
        .filter((day) => this.dayHasAvailability(day.key) && this.dayBreakCount(day.key) === 0)
        .map((day) => day.label);
    },
    shouldPromptForBreaks() {
      return this.breakRulesAreConfigured && this.recurringDaysMissingBreaks.length > 0;
    },
    breakGuidanceMessage() {
      if (!this.showBreakGuidancePanel) {
        return "";
      }

      return `Create breaks for: ${this.recurringDaysMissingBreaks.join(", ")}.`;
    },
    showBreakGuidancePanel() {
      return this.form.type === "recurring"
        && this.createWizardStep === 4
        && this.isBreakMode
        && this.shouldPromptForBreaks;
    },
  },
  mounted() {
    if (this.canUseDoctorCenter) {
      this.initialLoad();
    }
    this.applyRouteMode();
    window.addEventListener("mouseup", this.cancelAvailabilityPaint);
  },
  beforeDestroy() {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
    if (this.breakHoverTimer) {
      clearTimeout(this.breakHoverTimer);
    }
    window.removeEventListener("mouseup", this.cancelAvailabilityPaint);
  },
  methods: {
    async initialLoad(options = {}) {
      const preserveDate = options.preserveDate === true;
      this.loading = true;
      try {
        const [settingsResponse, servicesResponse, availabilityResponse] = await Promise.all([
          axios.get("/appointmentsettings"),
          axios.get("/doctorappointmentservices"),
          axios.get("/myappointmentavailabilities", {
            params: {
              search: this.searchTerm,
              page: this.pagination.current_page,
              per_page: this.pagination.per_page,
            },
          }),
        ]);

        this.settings = {
          max_breaks_per_availability: settingsResponse.data?.settings?.max_breaks_per_availability ?? 2,
          default_break_duration_minutes: settingsResponse.data?.settings?.default_break_duration_minutes ?? 15,
        };
        this.services = servicesResponse.data?.appointment_services || [];
        this.availabilities = availabilityResponse.data?.availabilities || [];
        this.blockingAvailabilities = availabilityResponse.data?.blocking_availabilities || [];
        this.calendarDays = availabilityResponse.data?.calendar_days || [];
        this.pagination = {
          ...this.pagination,
          ...(availabilityResponse.data?.pagination || {}),
        };

        if (!preserveDate) {
          const initialDate = this.resolveInitialCalendarDate();
          if (initialDate) {
            await this.selectCalendarDate(initialDate);
          }
        } else if (this.selectedCalendarDate) {
          await this.selectCalendarDate(this.selectedCalendarDate);
        }
      } catch (error) {
        this.showRequestError(error, "Failed to load the appointment doctor center.");
      } finally {
        this.loading = false;
      }
    },
    serviceLabel(item) {
      return `${item.service_name} (${item.service_category_name || "No category"})`;
    },
    async openCreateAvailabilityModal() {
      await this.$router.push("/appointments/doctor-center/create-availability");
    },
    closeCreateAvailabilityModal() {
      if (this.isCreateAvailabilityPage) {
        this.$router.push("/appointments/doctor-center");
        return;
      }
      this.showCreateAvailabilityModal = false;
      this.showSheetCellActionModal = false;
      this.activeSheetCell = null;
      this.validationErrors = {};
      this.isBreakMode = false;
      this.focusedBreakDay = "";
    },
    addBreak() {
      if (this.form.breaks.length >= this.settings.max_breaks_per_availability) {
        this.$toast.warning("You have reached the configured number of break windows.");
        return;
      }
      this.form.breaks.push({ break_start_time: "" });
    },
    removeBreak(index) {
      this.form.breaks.splice(index, 1);
    },
    clampOneOffBreak(index) {
      const item = this.form.breaks[index];
      if (!item) {
        return;
      }
      if (!item.break_start_time) {
        return;
      }
      if (!this.canAddOneOffBreaks || !this.isBreakWithinOneOffWindow(item.break_start_time)) {
        this.$set(this.form.breaks, index, {
          ...item,
          break_start_time: "",
        });
      }
    },
    queueSearch() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }
      this.searchTimer = setTimeout(() => {
        this.pagination.current_page = 1;
        this.initialLoad({ preserveDate: true });
      }, 350);
    },
    async changePage(page) {
      this.pagination.current_page = page;
      await this.initialLoad({ preserveDate: true });
    },
    async previewAvailability(showModal = false) {
      try {
        const response = await axios.post("/previewappointmentavailability", this.requestPayload());
        this.previewResult = response.data;
        return true;
      } catch (error) {
        this.previewResult = {
          ok: false,
          message: error?.response?.data?.message || "The availability has conflicts that must be corrected.",
          breaks: error?.response?.data?.breaks || [],
          conflicts: error?.response?.data?.conflicts || [],
        };
        if (showModal) {
          this.$bvModal.show("availability-preview-modal");
        }
        return false;
      }
    },
    async submitAvailability() {
      this.submitting = true;
      this.validationErrors = {};
      try {
        if (this.form.type === "recurring") {
          const patternPayload = this.buildWeeklyPatternPayload();
          if (!patternPayload.weekly_pattern.length) {
            this.$toast.error("Select recurring availability cells before submitting.");
            return;
          }

          if (this.breakRulesAreConfigured && this.recurringDaysMissingBreaks.length) {
            this.activateBreakGuidance();
            return;
          }

          const isClear = await this.previewRecurringPattern(patternPayload);
          if (!isClear) {
            this.$bvModal.show("availability-preview-modal");
            return;
          }

          const response = await axios.post("/saveappointmentweeklypattern", patternPayload);
          this.$toast.success(response.data?.message || "Weekly recurring availability submitted successfully.");
        } else {
          const isClear = await this.previewAvailability(false);
          if (!isClear) {
            this.$bvModal.show("availability-preview-modal");
            return;
          }

          const response = await axios.post("/saveappointmentavailability", this.requestPayload());
          this.$toast.success(response.data?.message || "Availability submitted successfully.");
        }

        this.$bvModal.hide("submit-availability-modal");
        this.resetForm();
        if (!this.isCreateAvailabilityPage) {
          this.closeCreateAvailabilityModal();
        }
        await this.initialLoad({ preserveDate: true });
        this.activeDoctorCenterTab = 1;
        if (this.isCreateAvailabilityPage) {
          await this.$router.push({ path: "/appointments/doctor-center", query: { tab: "availability" } });
        }
      } catch (error) {
        this.validationErrors = this.normalizeValidationErrors(error);
        this.showRequestError(error, "Failed to submit availability.");
      } finally {
        this.submitting = false;
      }
    },
    confirmWithdrawAvailability(item) {
      this.pendingWithdrawAvailability = item;
      this.showWithdrawConfirmModal = true;
    },
    cancelWithdrawConfirmation() {
      this.showWithdrawConfirmModal = false;
      this.pendingWithdrawAvailability = null;
    },
    async withdrawAvailability() {
      if (!this.pendingWithdrawAvailability) {
        return;
      }

      this.withdrawingId = this.pendingWithdrawAvailability.id;
      try {
        const response = await axios.post("/withdrawappointmentavailability", { id: this.pendingWithdrawAvailability.id });
        this.$toast.success(response.data?.message || "Availability withdrawn successfully.");
        this.cancelWithdrawConfirmation();
        await this.initialLoad({ preserveDate: true });
      } catch (error) {
        this.showRequestError(error, "Failed to withdraw availability.");
      } finally {
        this.withdrawingId = null;
      }
    },
    async selectCalendarDate(date) {
      this.selectedCalendarDate = date;
      this.loadingDaySlots = true;
      try {
        const [slotResponse, bookingResponse] = await Promise.all([
          axios.get("/myappointmentslotday", { params: { slot_date: date } }),
          axios.get("/appointmentbookingday", { params: { slot_date: date } }),
        ]);
        this.daySlots = slotResponse.data?.slots || [];
        this.daySlotBookings = bookingResponse.data?.bookings || [];
      } catch (error) {
        this.daySlotBookings = [];
        this.showRequestError(error, "Failed to load your slots for the selected day.");
      } finally {
        this.loadingDaySlots = false;
      }
    },
    requestPayload() {
      return {
        appointment_service_id: this.selectedService?.id || "",
        type: this.form.type,
        day_of_week: null,
        specific_date: this.form.type === "one_off" ? this.form.specific_date : null,
        start_date: this.form.specific_date || null,
        end_date: this.form.specific_date || null,
        start_time: this.form.start_time,
        end_time: this.form.end_time,
        notes: this.form.notes,
        breaks: this.form.breaks.filter((item) => item.break_start_time),
      };
    },
    resolveInitialCalendarDate() {
      const todaySlotDate = this.calendarDays.find((item) => item.slot_date === this.todayIso)?.slot_date;
      return todaySlotDate || this.todayIso;
    },
    slotPopoverTarget(slot) {
      return `doctor-slot-card-${slot.id}`;
    },
    slotBookingDetails(slot) {
      const booking = this.daySlotBookingsBySlotId[slot.id]
        || slot.booking
        || slot.appointment_booking
        || slot.booked_patient
        || slot.patient
        || {};
      const patient = booking.patient || slot.patient || {};
      const rows = [
        {
          label: "Patient",
          value: slot.patient_name || booking.patient_name || booking.name || patient.name || patient.full_name || "",
        },
        {
          label: "Patient ID",
          value: slot.patient_id || booking.patient_id || patient.patient_id || patient.id || "",
        },
        {
          label: "Telephone",
          value: slot.telephone || slot.phone_number || booking.patient_contact || booking.telephone || booking.phone_number || patient.telephone || patient.phone_number || "",
        },
        {
          label: "Booking Ref",
          value: slot.booking_reference || slot.reference || booking.reference || booking.booking_reference || booking.public_id || "",
        },
        {
          label: "Booked On",
          value: slot.booked_at || booking.booked_at || booking.created_at || "",
        },
      ].filter((detail) => detail.value);

      return rows.map((detail) => ({
        ...detail,
        value: detail.label === "Booked On" ? this.formatDateTime(detail.value) : detail.value,
      }));
    },
    resetForm() {
      this.selectedService = null;
      this.validationErrors = {};
      this.activeSheetCell = null;
      this.activeBreakQuarter = 0;
      this.isBreakMode = false;
      this.focusedBreakDay = "";
      this.isSheetPointerDown = false;
      this.isDraggingAvailability = false;
      this.dragOriginKey = "";
      this.dragVisitedKeys = [];
      this.form = {
        type: "",
        specific_date: "",
        start_time: "",
        end_time: "",
        breaks: [],
        notes: "",
      };
      this.recurringSheet = {
        start_date: "",
        end_date: "",
        selections: {},
      };
      this.createWizardStep = 1;
    },
    handleServiceSelection() {
      delete this.validationErrors.appointment_service_id;
      if (this.createWizardStep === 1 && this.selectedService?.id) {
        this.createWizardStep = 2;
      }
    },
    selectAvailabilityType(type) {
      this.form.type = type;
      delete this.validationErrors.type;
      if (this.createWizardStep === 2 && this.form.type) {
        this.createWizardStep = 3;
      }
      if (type === "one_off" && this.form.end_time && this.form.start_time && this.timeToMinutes(this.form.end_time) < this.timeToMinutes(this.oneOffMinimumEndTime)) {
        this.form.end_time = "";
      }
    },
    shiftMonth(step) {
      const next = new Date(this.currentMonth);
      next.setMonth(next.getMonth() + step);
      this.currentMonth = this.startOfMonth(next);
    },
    async applyRouteMode() {
      this.activeDoctorCenterTab = this.$route.query.tab === "availability" ? 1 : 0;
      if (!this.canUseDoctorCenter) {
        return;
      }
      if (this.isCreateAvailabilityPage) {
        await this.syncDoctorAvailabilityState();
        this.resetForm();
        this.activeBreakQuarter = 0;
        this.showCreateAvailabilityModal = false;
      } else {
        this.showSheetCellActionModal = false;
        this.activeSheetCell = null;
      }
    },
    startOfMonth(date) {
      return new Date(date.getFullYear(), date.getMonth(), 1);
    },
    isoDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    computedBreakEnd(startTime) {
      if (!startTime) return "";
      const [hours, minutes] = startTime.split(":").map(Number);
      const date = new Date();
      date.setHours(hours, minutes, 0, 0);
      date.setMinutes(date.getMinutes() + Number(this.settings.default_break_duration_minutes || 0));
      return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
    },
    isBreakWithinOneOffWindow(startTime) {
      if (!startTime || !this.canAddOneOffBreaks) {
        return false;
      }
      const breakEnd = this.computedBreakEnd(startTime);
      return this.timeToMinutes(startTime) >= this.timeToMinutes(this.form.start_time)
        && this.timeToMinutes(breakEnd) <= this.timeToMinutes(this.form.end_time);
    },
    oneOffBreakState(startTime) {
      if (!startTime) {
        return "empty";
      }
      if (!this.canAddOneOffBreaks) {
        return "invalid";
      }
      return this.isBreakWithinOneOffWindow(startTime) ? "valid" : "invalid";
    },
    formatStatus(value) {
      return String(value || "").replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
    },
    statusBadgeClass(status) {
      if (status === "approved") return "bg-soft-success text-success";
      if (status === "rejected") return "bg-soft-danger text-danger";
      if (status === "withdrawn") return "bg-soft-secondary text-secondary";
      return "bg-soft-warning text-warning";
    },
    scheduleLabel(item) {
      if (item.type === "recurring") {
        return `${this.titleCase(item.day_of_week)} recurring availability`;
      }
      return "One-time availability";
    },
    scheduleWindowLabel(item) {
      if (item.type === "recurring") {
        return `${this.formatDate(item.start_date)}${item.end_date ? ` to ${this.formatDate(item.end_date)}` : ""} • ${this.formatTimeRange(item.start_time, item.end_time)}`;
      }
      return `${this.formatDate(item.specific_date)} • ${this.formatTimeRange(item.start_time, item.end_time)}`;
    },
    formatDate(value) {
      if (!value) return "";
      return new Date(value).toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    },
    formatDateTime(value) {
      if (!value) return "";
      return new Date(value).toLocaleString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    },
    formatTime(value) {
      if (!value) return "";
      const [hours, minutes] = String(value).split(":");
      const date = new Date();
      date.setHours(Number(hours), Number(minutes), 0, 0);
      return date.toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "2-digit",
      });
    },
    formatTimeRange(start, end) {
      return `${this.formatTime(start)} - ${this.formatTime(end)}`;
    },
    titleCase(value) {
      return String(value || "").replace(/\b\w/g, (letter) => letter.toUpperCase());
    },
    money(value) {
      return Number(value || 0).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    normalizeValidationErrors(error) {
      const errors = error?.response?.data?.errors || {};
      return Object.keys(errors).reduce((acc, key) => {
        acc[key] = Array.isArray(errors[key]) ? errors[key][0] : errors[key];
        return acc;
      }, {});
    },
    showRequestError(error, fallbackMessage) {
      const message = error?.response?.data?.message || Object.values(error?.response?.data?.errors || {})?.[0]?.[0] || fallbackMessage;
      this.$toast.error(message);
    },
    async previewPayload(payload) {
      try {
        const response = await axios.post("/previewappointmentavailability", payload);
        this.previewResult = response.data;
        return true;
      } catch (error) {
        this.previewResult = {
          ok: false,
          message: error?.response?.data?.message || "The availability has conflicts that must be corrected.",
          breaks: error?.response?.data?.breaks || [],
          conflicts: error?.response?.data?.conflicts || [],
        };
        return false;
      }
    },
    async previewRecurringPattern(payload) {
      try {
        await this.syncDoctorAvailabilityState();
        const response = await axios.post("/previewappointmentweeklypattern", payload);
        this.previewResult = response.data;
        return true;
      } catch (error) {
        this.previewResult = {
          ok: false,
          message: error?.response?.data?.message || "The weekly recurring availability has conflicts that must be corrected.",
          conflicts: error?.response?.data?.conflicts || [],
        };
        return false;
      }
    },
    async syncDoctorAvailabilityState() {
      try {
        const response = await axios.get("/myappointmentavailabilities", {
          params: {
            search: this.searchTerm,
            page: this.pagination.current_page,
            per_page: this.pagination.per_page,
          },
        });

        this.availabilities = response.data?.availabilities || this.availabilities;
        this.blockingAvailabilities = response.data?.blocking_availabilities || [];
        this.calendarDays = response.data?.calendar_days || this.calendarDays;
        this.pagination = {
          ...this.pagination,
          ...(response.data?.pagination || {}),
        };
      } catch (error) {
        this.showRequestError(error, "Failed to refresh availability data.");
      }
    },
    buildWeeklyPatternPayload() {
      const groupedPattern = this.recurringDays.reduce((acc, day) => {
        acc[day.key] = [];
        return acc;
      }, {});

      this.recurringDays.forEach((day) => {
        this.buildRawDaySegments(day.key).forEach((segment) => {
          const payloadSegment = this.normalizeRecurringSegmentForPayload(day.key, segment);
          if (payloadSegment) {
            groupedPattern[payloadSegment.day_of_week].push(payloadSegment.segment);
          }
        });
      });

      return {
        appointment_service_id: this.selectedService?.id || "",
        start_date: this.recurringSheet.start_date,
        end_date: this.recurringSheet.end_date,
        notes: this.form.notes,
        weekly_pattern: this.recurringDays
          .map((day) => ({
            day_of_week: day.key,
            segments: groupedPattern[day.key]
              .sort((left, right) => this.timeToMinutes(left.start_time) - this.timeToMinutes(right.start_time)),
          }))
          .filter((day) => day.segments.length > 0),
      };
    },
    toggleBreakMode() {
      this.isBreakMode = !this.isBreakMode;
      if (!this.isBreakMode) {
        this.showSheetCellActionModal = false;
        this.activeSheetCell = null;
        this.focusedBreakDay = "";
      }
    },
    activateBreakGuidanceForDay(dayKey) {
      this.focusedBreakDay = dayKey;
      this.isBreakMode = true;
      this.showSheetCellActionModal = false;
      this.activeSheetCell = null;
      this.$toast.warning(`Create at least one break for ${this.titleCase(dayKey)}.`);
    },
    finishBreakGuidanceForDay(dayKey) {
      if (this.focusedBreakDay === dayKey) {
        this.focusedBreakDay = "";
      }
      this.isBreakMode = false;
      this.showSheetCellActionModal = false;
      this.activeSheetCell = null;
    },
    handleCreateWizardPrimaryAction() {
      if (this.isFinalCreateStep) {
        this.openSubmitAvailabilityModal();
        return;
      }
      this.nextCreateStep();
    },
    openSubmitAvailabilityModal() {
      if (this.submitting) {
        return;
      }
      this.$bvModal.show("submit-availability-modal");
    },
    async confirmSubmitAvailability() {
      await this.submitAvailability();
    },
    nextCreateStep() {
      this.validationErrors = {};
      const error = this.validateCurrentStep();
      if (error) {
        this.$toast.warning(error);
        return;
      }
      if (this.form.type === "recurring" && this.createWizardStep === 4 && this.shouldPromptForBreaks) {
        this.activateBreakGuidance();
        return;
      }
      this.createWizardStep += 1;
    },
    previousCreateStep() {
      if (this.createWizardStep > 1) {
        this.createWizardStep -= 1;
      }
    },
    validateCurrentStep() {
      if (this.createWizardStep === 1) {
        if (!this.selectedService?.id) {
          this.validationErrors.appointment_service_id = "Select a bookable service.";
          return "Select a bookable service before continuing.";
        }
        return "";
      }
      if (this.createWizardStep === 2 && !this.form.type) {
        this.validationErrors.type = "Choose an availability type.";
        return "Choose an availability type before continuing.";
      }
      if (this.form.type === "one_off" && this.createWizardStep === 3) {
        if (!this.form.specific_date || !this.form.start_time || !this.form.end_time) {
          if (!this.form.specific_date) this.validationErrors.specific_date = "Choose the availability date.";
          if (!this.form.start_time) this.validationErrors.start_time = "Choose the start time.";
          if (!this.form.end_time) this.validationErrors.end_time = "Choose the end time.";
          return "Set the availability date, start time, and end time before continuing.";
        }
        if (this.form.specific_date < this.todayIso) {
          this.validationErrors.specific_date = "Choose today or a future date.";
          return "Availability cannot be created in the past.";
        }
        if (this.timeToMinutes(this.form.end_time) <= this.timeToMinutes(this.form.start_time)) {
          this.validationErrors.end_time = "Choose a time later than the start time.";
          return "The end time must be later than the start time.";
        }
        if ((this.timeToMinutes(this.form.end_time) - this.timeToMinutes(this.form.start_time)) < 60) {
          this.validationErrors.end_time = "End time must be at least 1 hour after the start time.";
          return "Set a time range that is at least 1 hour long.";
        }
      }
      if (this.form.type === "recurring" && this.createWizardStep === 3) {
        if (!this.recurringSheet.start_date || !this.recurringSheet.end_date) {
          if (!this.recurringSheet.start_date) this.validationErrors.start_date = "Choose the start date.";
          if (!this.recurringSheet.end_date) this.validationErrors.end_date = "Choose the end date.";
          return "Set the recurring date range before continuing.";
        }
        if (this.recurringSheet.start_date < this.todayIso) {
          this.validationErrors.start_date = "Choose today or a future date.";
          return "Recurring availability cannot start in the past.";
        }
        if (this.recurringSheet.end_date < this.recurringMinimumEndDate) {
          this.validationErrors.end_date = "End date must be on or after the start date.";
          return "Choose an end date that comes after the start date.";
        }
      }
      if (this.form.type === "recurring" && this.createWizardStep === 4) {
        if (!this.hasRecurringAvailabilitySelections()) {
          return "Tap the sheet and mark at least one availability block before continuing.";
        }
      }
      return "";
    },
    activateBreakGuidance() {
      this.isBreakMode = true;
      this.focusedBreakDay = this.recurringDays.find((day) => this.dayNeedsBreak(day.key))?.key || "";
      this.showSheetCellActionModal = false;
      this.activeSheetCell = null;
      this.$toast.warning(`Create breaks for: ${this.recurringDaysMissingBreaks.join(", ")}.`);
    },
    segmentKey(dayKey, hourStart, quarterIndex) {
      return `${dayKey}-${hourStart}-${quarterIndex}`;
    },
    quarterStart(hourStart, quarterIndex) {
      return this.minutesToTime(this.timeToMinutes(hourStart) + (quarterIndex * 15));
    },
    quarterEnd(hourStart, quarterIndex) {
      return this.minutesToTime(this.timeToMinutes(hourStart) + ((quarterIndex + 1) * 15));
    },
    sheetQuarterState(dayKey, column, quarterIndex) {
      const key = this.segmentKey(dayKey, column.start, quarterIndex);
      if (this.occupiedRecurringCells[key]) {
        return "occupied";
      }
      return this.recurringSheet.selections[key] || "free";
    },
    sheetQuarterClass(dayKey, column, quarterIndex) {
      return this.sheetQuarterState(dayKey, column, quarterIndex);
    },
    hourQuarterStates(dayKey, column) {
      return this.quarterSegments.map((quarter) => this.sheetQuarterState(dayKey, column, quarter.index));
    },
    shouldRenderQuarterSegments(dayKey, column) {
      const states = this.hourQuarterStates(dayKey, column);
      const uniqueStates = Array.from(new Set(states));
      if (uniqueStates.length > 1) {
        return true;
      }

      return uniqueStates[0] === "break";
    },
    sheetHourState(dayKey, column) {
      const states = this.hourQuarterStates(dayKey, column);
      return Array.from(new Set(states))[0] || "free";
    },
    sheetHourClass(dayKey, column) {
      return this.sheetHourState(dayKey, column);
    },
    sheetHourTitle(dayKey, column) {
      const state = this.sheetHourState(dayKey, column);
      const range = `${column.start} - ${column.end}`;
      if (state === "occupied") return `${range} | Already taken`;
      if (state === "availability") return `${range} | Availability selected`;
      return `${range} | Click or drag to mark the whole hour as availability`;
    },
    sheetQuarterTitle(dayKey, column, quarterIndex) {
      const state = this.sheetQuarterState(dayKey, column, quarterIndex);
      const range = `${this.quarterStart(column.start, quarterIndex)} - ${this.quarterEnd(column.start, quarterIndex)}`;
      if (state === "occupied") return `${range} | ${this.occupiedRecurringCells[this.segmentKey(dayKey, column.start, quarterIndex)]}`;
      if (state === "availability") return `${range} | Availability selected`;
      if (state === "break") return `${range} | Break selected`;
      return `${range} | Click to choose or drag to paint availability`;
    },
    scheduleBreakHover(dayKey, hourStart, quarterIndex, mode) {
      if (this.isSheetPointerDown || this.isBreakMode) {
        return;
      }

      this.clearBreakHover();
      if (this.remainingBreaksForDay(dayKey) <= 0) {
        return;
      }
      const check = this.canPlaceBreakAt(dayKey, hourStart, quarterIndex);
      if (!check.ok) {
        return;
      }

      this.breakHoverTimer = setTimeout(() => {
        this.breakHoverTarget = { dayKey, hourStart, quarterIndex, mode };
      }, 550);
    },
    clearBreakHover() {
      if (this.breakHoverTimer) {
        clearTimeout(this.breakHoverTimer);
        this.breakHoverTimer = null;
      }
      this.breakHoverTarget = null;
    },
    isBreakHoverTarget(dayKey, hourStart, quarterIndex, mode) {
      return this.breakHoverTarget
        && this.breakHoverTarget.dayKey === dayKey
        && this.breakHoverTarget.hourStart === hourStart
        && this.breakHoverTarget.quarterIndex === quarterIndex
        && this.breakHoverTarget.mode === mode;
    },
    startAvailabilityPaint(dayKey, column, quarterIndex) {
      if (this.isBreakHoverTarget(dayKey, column.start, quarterIndex, "quarter")) {
        this.createBreakAt(dayKey, column.start, quarterIndex);
        return;
      }

      this.clearBreakHover();
      if (this.sheetQuarterState(dayKey, column, quarterIndex) === "occupied") {
        return;
      }

      const key = this.segmentKey(dayKey, column.start, quarterIndex);
      this.isSheetPointerDown = true;
      this.isDraggingAvailability = false;
      this.dragOriginKey = key;
      this.dragVisitedKeys = [key];
      this.activeSheetCell = {
        day: dayKey,
        hourStart: column.start,
        hourLabel: column.label,
        quarterIndex,
        label: `${this.titleCase(dayKey)} | ${column.label}`,
      };
      this.activeBreakQuarter = quarterIndex;

      if (this.isBreakMode) {
        this.showSheetCellActionModal = true;
      }
    },
    extendAvailabilityPaint(dayKey, column, quarterIndex) {
      if (!this.isSheetPointerDown) {
        return;
      }

      if (this.isBreakMode) {
        return;
      }

      const key = this.segmentKey(dayKey, column.start, quarterIndex);
      if (this.dragVisitedKeys.includes(key)) {
        return;
      }

      if (!this.isDraggingAvailability && this.dragOriginKey) {
        const [originDay, originHour, originQuarter] = this.dragOriginKey.split("-");
        this.paintSegmentAvailability(originDay, originHour, Number(originQuarter));
      }

      this.isDraggingAvailability = true;
      this.dragVisitedKeys.push(key);
      this.paintSegmentAvailability(dayKey, column.start, quarterIndex);
    },
    finishAvailabilityPaint(dayKey, column, quarterIndex) {
      if (!this.isSheetPointerDown) {
        return;
      }

      if (this.isBreakMode) {
        this.cancelAvailabilityPaint();
        return;
      }

      if (!this.isDraggingAvailability) {
        if (this.isBreakHoverTarget(dayKey, column.start, quarterIndex, "quarter")) {
          this.createBreakAt(dayKey, column.start, quarterIndex);
        } else {
          this.toggleSegmentAvailability(dayKey, column.start, quarterIndex);
        }
      } else {
        this.paintSegmentAvailability(dayKey, column.start, quarterIndex);
      }

      this.cancelAvailabilityPaint();
    },
    cancelAvailabilityPaint() {
      this.isSheetPointerDown = false;
      this.isDraggingAvailability = false;
      this.dragOriginKey = "";
      this.dragVisitedKeys = [];
    },
    toggleSegmentAvailability(dayKey, hourStart, quarterIndex) {
      const key = this.segmentKey(dayKey, hourStart, quarterIndex);
      if (this.occupiedRecurringCells[key]) {
        return;
      }
      if (this.recurringSheet.selections[key] === "availability") {
        this.$delete(this.recurringSheet.selections, key);
        return;
      }
      this.$set(this.recurringSheet.selections, key, "availability");
    },
    paintHourAvailability(dayKey, hourStart) {
      this.quarterSegments.forEach((quarter) => {
        this.paintSegmentAvailability(dayKey, hourStart, quarter.index);
      });
    },
    startHourAvailabilityPaint(dayKey, column) {
      if (this.isBreakHoverTarget(dayKey, column.start, 0, "hour")) {
        this.createBreakAt(dayKey, column.start, 0);
        return;
      }

      this.clearBreakHover();
      if (this.sheetHourState(dayKey, column) === "occupied") {
        return;
      }

      if (this.isBreakMode) {
        this.activeSheetCell = {
          day: dayKey,
          hourStart: column.start,
          hourLabel: column.label,
          quarterIndex: 0,
          label: `${this.titleCase(dayKey)} | ${column.label}`,
        };
        this.activeBreakQuarter = 0;
        this.showSheetCellActionModal = true;
        return;
      }

      const key = `${dayKey}|${column.start}|hour`;
      this.isSheetPointerDown = true;
      this.isDraggingAvailability = false;
      this.dragOriginKey = key;
      this.dragVisitedKeys = [key];
    },
    extendHourAvailabilityPaint(dayKey, column) {
      if (!this.isSheetPointerDown) {
        return;
      }

      const key = `${dayKey}|${column.start}|hour`;
      if (this.dragVisitedKeys.includes(key)) {
        return;
      }

      if (!this.isDraggingAvailability && this.dragOriginKey) {
        const [originDay, originHour] = this.dragOriginKey.split("|");
        this.paintHourAvailability(originDay, originHour);
      }

      this.isDraggingAvailability = true;
      this.dragVisitedKeys.push(key);
      this.paintHourAvailability(dayKey, column.start);
    },
    finishHourAvailabilityPaint(dayKey, column) {
      if (!this.isSheetPointerDown) {
        return;
      }

      if (!this.isDraggingAvailability && !this.isBreakMode) {
        if (this.isBreakHoverTarget(dayKey, column.start, 0, "hour")) {
          this.createBreakAt(dayKey, column.start, 0);
        } else if (this.sheetHourState(dayKey, column) === "availability") {
          this.clearHourSelection(dayKey, column.start);
        } else {
          this.paintHourAvailability(dayKey, column.start);
        }
      } else {
        this.paintHourAvailability(dayKey, column.start);
      }
      this.cancelAvailabilityPaint();
    },
    clearHourSelection(dayKey, hourStart) {
      this.quarterSegments.forEach((quarter) => {
        const key = this.segmentKey(dayKey, hourStart, quarter.index);
        if (this.recurringSheet.selections[key] === "availability") {
          this.$delete(this.recurringSheet.selections, key);
        }
      });
    },
    paintSegmentAvailability(dayKey, hourStart, quarterIndex) {
      const key = this.segmentKey(dayKey, hourStart, quarterIndex);
      if (this.occupiedRecurringCells[key]) {
        return;
      }
      this.$set(this.recurringSheet.selections, key, "availability");
    },
    createBreakAt(dayKey, hourStart, quarterIndex) {
      const check = this.canPlaceBreakAt(dayKey, hourStart, quarterIndex);
      if (!check.ok) {
        this.clearBreakHover();
        this.$toast.error(check.message);
        return;
      }

      this.activeSheetCell = {
        day: dayKey,
        hourStart,
        hourLabel: "",
        quarterIndex,
        label: `${this.titleCase(dayKey)} | ${hourStart}`,
      };
      this.activeBreakQuarter = quarterIndex;
      this.applyBreakFromActionCell();
      this.clearBreakHover();
    },
    applyAvailabilityFromActionCell() {
      if (!this.activeSheetCell) {
        return;
      }

      this.paintSegmentAvailability(this.activeSheetCell.day, this.activeSheetCell.hourStart, this.activeSheetCell.quarterIndex);
      this.showSheetCellActionModal = false;
      this.activeSheetCell = null;
    },
    canPlaceBreakAt(dayKey, hourStart, quarterIndex) {
      const breakCount = this.dayBreakCount(dayKey);
      const targetKey = this.segmentKey(dayKey, hourStart, quarterIndex);
      const targetState = this.recurringSheet.selections[targetKey] || "free";

      if (targetState !== "break" && breakCount >= Number(this.settings.max_breaks_per_availability || 0)) {
        return { ok: false, message: "You have reached the maximum number of break windows for this day." };
      }

      const duration = Number(this.settings.default_break_duration_minutes || 0);
      const steps = Math.max(Math.ceil(duration / 15), 1);
      let cursor = this.timeToMinutes(this.quarterStart(hourStart, quarterIndex));

      for (let index = 0; index < steps; index += 1) {
        const segmentHour = this.minutesToTime(Math.floor(cursor / 60) * 60);
        const segmentQuarter = Math.floor((cursor % 60) / 15);
        const key = this.segmentKey(dayKey, segmentHour, segmentQuarter);
        if (this.occupiedRecurringCells[key]) {
          return { ok: false, message: "Break time cannot overlap already allocated red time." };
        }
        cursor += 15;
      }

      return { ok: true };
    },
    applyBreakFromActionCell() {
      if (!this.activeSheetCell) {
        return;
      }

      const check = this.canPlaceBreakAt(this.activeSheetCell.day, this.activeSheetCell.hourStart, this.activeBreakQuarter);
      if (!check.ok) {
        this.$toast.error(check.message);
        return;
      }

      const duration = Number(this.settings.default_break_duration_minutes || 0);
      const steps = Math.max(Math.ceil(duration / 15), 1);
      let cursor = this.timeToMinutes(this.quarterStart(this.activeSheetCell.hourStart, this.activeBreakQuarter));

      for (let index = 0; index < steps; index += 1) {
        const segmentHour = this.minutesToTime(Math.floor(cursor / 60) * 60);
        const segmentQuarter = Math.floor((cursor % 60) / 15);
        const key = this.segmentKey(this.activeSheetCell.day, segmentHour, segmentQuarter);

        if (!this.occupiedRecurringCells[key]) {
          this.$set(this.recurringSheet.selections, key, "break");
        }

        cursor += 15;
      }

      this.showSheetCellActionModal = false;
      this.activeSheetCell = null;
    },
    clearActionCellSelection() {
      if (!this.activeSheetCell) {
        return;
      }

      this.quarterSegments.forEach((quarter) => {
        const key = this.segmentKey(this.activeSheetCell.day, this.activeSheetCell.hourStart, quarter.index);
        if (this.recurringSheet.selections[key]) {
          this.$delete(this.recurringSheet.selections, key);
        }
      });

      this.showSheetCellActionModal = false;
      this.activeSheetCell = null;
    },
    hasRecurringAvailabilitySelections() {
      return Object.values(this.recurringSheet.selections).some((value) => value === "availability");
    },
    dayHasAvailability(dayKey) {
      return this.buildDaySegments(dayKey).some((segment) => segment.type === "availability");
    },
    dayNeedsBreak(dayKey) {
      return this.breakRulesAreConfigured && this.dayHasAvailability(dayKey) && this.dayBreakCount(dayKey) === 0;
    },
    shouldShowDayBreakActions(dayKey) {
      return this.dayNeedsBreak(dayKey) || (this.isBreakMode && this.focusedBreakDay === dayKey);
    },
    dayRowClass(dayKey) {
      return {
        "day-warning-row": this.dayNeedsBreak(dayKey),
        "day-break-focus-row": this.isBreakMode && this.focusedBreakDay === dayKey,
      };
    },
    dayHeaderClass(dayKey) {
      return {
        "day-warning-header": this.dayNeedsBreak(dayKey),
      };
    },
    dayBreakUsageClass(dayKey) {
      return this.dayNeedsBreak(dayKey) ? "day-break-usage warning" : "text-muted";
    },
    dayHasRecurringSelections(dayKey) {
      return Object.keys(this.recurringSheet.selections).some((key) => key.startsWith(`${dayKey}-`));
    },
    dayBreakCount(dayKey) {
      const cells = this.hourlyColumns.flatMap((column) => this.quarterSegments.map((quarter) => this.recurringSheet.selections[this.segmentKey(dayKey, column.start, quarter.index)] || "free"));
      let count = 0;
      let open = false;
      cells.forEach((cellType) => {
        if (cellType === "break" && !open) {
          count += 1;
          open = true;
        } else if (cellType !== "break") {
          open = false;
        }
      });
      return count;
    },
    dayBreakUsage(dayKey) {
      return `${this.dayBreakCount(dayKey)}/${Number(this.settings.max_breaks_per_availability || 0)} breaks`;
    },
    remainingBreaksForDay(dayKey) {
      return Math.max(Number(this.settings.max_breaks_per_availability || 0) - this.dayBreakCount(dayKey), 0);
    },
    buildRawDaySegments(dayKey) {
      const cells = this.hourlyColumns.flatMap((column) => this.quarterSegments.map((quarter) => ({
        start_time: this.quarterStart(column.start, quarter.index),
        end_time: this.quarterEnd(column.start, quarter.index),
        type: this.recurringSheet.selections[this.segmentKey(dayKey, column.start, quarter.index)] || "free",
      })));

      const segments = [];
      let current = null;

      cells.forEach((cell) => {
        if (cell.type === "free") {
          if (current) {
            segments.push(current);
            current = null;
          }
          return;
        }

        if (current && current.type === cell.type && current.end_time === cell.start_time) {
          current.end_time = cell.end_time;
          return;
        }

        if (current) {
          segments.push(current);
        }

        current = { ...cell };
      });

      if (current) {
        segments.push(current);
      }

      return segments;
    },
    buildDaySegments(dayKey) {
      const segments = this.buildRawDaySegments(dayKey);

      return segments.map((segment) => ({
        ...segment,
        start_time: this.normalizeClockTime(segment.start_time),
        end_time: this.normalizeClockTime(segment.end_time),
      }));
    },
    normalizeRecurringSegmentForPayload(dayKey, segment) {
      const startMinutes = this.timeToMinutes(segment.start_time);
      const endMinutes = this.timeToMinutes(segment.end_time);
      const nextDayCutoffMinutes = 6 * 60;
      const targetDay = startMinutes < nextDayCutoffMinutes ? this.nextRecurringDayKey(dayKey) : dayKey;

      return {
        day_of_week: targetDay,
        segment: {
          type: segment.type,
          start_time: this.normalizeClockTime(segment.start_time),
          end_time: endMinutes === 1440 ? "23:59" : this.normalizeClockTime(segment.end_time),
        },
      };
    },
    nextRecurringDayKey(dayKey) {
      const dayKeys = this.recurringDays.map((day) => day.key);
      const currentIndex = dayKeys.indexOf(dayKey);
      if (currentIndex === -1) {
        return dayKey;
      }
      return dayKeys[(currentIndex + 1) % dayKeys.length];
    },
    dayKeyFromDate(value) {
      if (!value) return "";
      return ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"][new Date(value).getDay()];
    },
    timeRangesOverlap(startA, endA, startB, endB) {
      const aStart = this.timeToMinutes(startA);
      const aEnd = this.timeToMinutes(endA);
      const bStart = this.timeToMinutes(startB);
      const bEnd = this.timeToMinutes(endB);
      return aStart < bEnd && bStart < aEnd;
    },
    timeToMinutes(value) {
      const [hours, minutes] = String(value || "00:00").split(":").map(Number);
      return (hours * 60) + minutes;
    },
    normalizeClockTime(value) {
      const totalMinutes = this.timeToMinutes(value);
      const normalizedMinutes = ((totalMinutes % 1440) + 1440) % 1440;
      const hours = Math.floor(normalizedMinutes / 60);
      const minutes = normalizedMinutes % 60;
      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
    },
    minutesToTime(minutes) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
    },
  },
  watch: {
    "$route.fullPath"() {
      this.applyRouteMode();
    },
    "form.specific_date"(value) {
      if (value && value < this.todayIso) {
        this.form.specific_date = this.todayIso;
      }
    },
    "form.start_time"() {
      if (this.form.end_time && this.timeToMinutes(this.form.end_time) <= this.timeToMinutes(this.form.start_time)) {
        this.form.end_time = "";
      }
      this.form.breaks = this.form.breaks.map((item) => (
        item.break_start_time && !this.isBreakWithinOneOffWindow(item.break_start_time)
          ? { ...item, break_start_time: "" }
          : item
      ));
    },
    "form.end_time"() {
      this.form.breaks = this.form.breaks.map((item) => (
        item.break_start_time && !this.isBreakWithinOneOffWindow(item.break_start_time)
          ? { ...item, break_start_time: "" }
          : item
      ));
    },
    "recurringSheet.start_date"(value) {
      if (value && value < this.todayIso) {
        this.recurringSheet.start_date = this.todayIso;
        return;
      }
      if (this.recurringSheet.end_date && this.recurringSheet.end_date < this.recurringMinimumEndDate) {
        this.recurringSheet.end_date = this.recurringMinimumEndDate;
      }
    },
    "recurringSheet.end_date"(value) {
      if (value && value < this.recurringMinimumEndDate) {
        this.recurringSheet.end_date = this.recurringMinimumEndDate;
      }
    },
  },
};
</script>

<style scoped>
.section-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.section-head-inline {
  align-items: center;
}

.availability-create-card,
.data-table-card,
.calendar-shell-card {
  border-radius: 1rem;
}

.create-availability-page-shell {
  width: 100%;
  padding: 0 0 0.4rem;
}

.doctor-center-tabs {
  gap: 0.75rem;
  border-bottom: 0;
}

.doctor-center-tabs .nav-item {
  margin-bottom: 0;
}

.doctor-center-tabs .nav-link {
  border: 1px solid #d9e3ee;
  border-radius: 999px;
  color: #52637a;
  background: #fff;
  padding: 0.7rem 1.15rem;
  font-weight: 600;
  transition: all 0.2s ease;
}

.doctor-center-tabs .nav-link.active {
  color: #fff;
  background: linear-gradient(135deg, #03967d 0%, #14b8a6 100%);
  border-color: transparent;
  box-shadow: 0 10px 26px rgba(3, 150, 125, 0.22);
}

.doctor-center-tabs .nav-link:hover:not(.active) {
  color: #0f172a;
  border-color: #b8c7d8;
}

.create-availability-modal :deep(.modal-dialog) {
  max-width: 940px;
  margin: 1rem auto;
}

.create-availability-modal :deep(.modal-content) {
  border-radius: 1.2rem;
  overflow: hidden;
  max-height: calc(100vh - 2rem);
  display: flex;
  flex-direction: column;
  background:
    radial-gradient(circle at top right, rgba(20, 184, 166, 0.08), transparent 28%),
    linear-gradient(180deg, #ffffff 0%, #f8fbfd 100%);
}

.create-availability-modal :deep(.modal-body) {
  padding: 1.35rem 1.4rem 1.45rem;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.wizard-progress {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.wizard-progress-page {
  margin-top: -0.1rem;
}

.wizard-progress-top {
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  gap: 1rem;
}

.wizard-progress-label {
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #03967d;
  margin-bottom: 0.25rem;
}

.wizard-progress-title {
  color: #0f172a;
}

.wizard-service-chip {
  min-width: 220px;
  max-width: 320px;
  padding: 0.72rem 0.9rem;
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.5);
  border: 1px solid rgba(148, 163, 184, 0.18);
  backdrop-filter: blur(6px);
}

.wizard-service-chip-label {
  display: block;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64748b;
  margin-bottom: 0.18rem;
}

.wizard-stepper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.4rem;
  position: relative;
  padding: 0.15rem 0;
}

.wizard-stepper-item {
  display: flex;
  flex: 1 1 0;
  align-items: center;
  gap: 0.7rem;
  min-width: 0;
  padding: 0 0.15rem;
  background: transparent;
  border: 0;
  position: relative;
  transition: transform 0.22s ease, opacity 0.22s ease;
}

.wizard-stepper-item::after {
  content: "";
  position: absolute;
  top: 1rem;
  left: calc(50% + 1.25rem);
  right: calc(-50% + 1.25rem);
  height: 1px;
  background: rgba(148, 163, 184, 0.45);
}

.wizard-stepper-item:last-child::after {
  display: none;
}

.wizard-stepper-item.active {
  transform: translateY(-1px);
}

.wizard-stepper-item.complete {
  opacity: 0.92;
}

.wizard-stepper-item.complete::after {
  background: linear-gradient(90deg, rgba(3, 150, 125, 0.65), rgba(20, 184, 166, 0.4));
}

.wizard-stepper-number {
  width: 2rem;
  height: 2rem;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(148, 163, 184, 0.35);
  color: #334155;
  font-size: 0.82rem;
  font-weight: 700;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
}

.wizard-stepper-item.active .wizard-stepper-number {
  border-color: rgba(3, 150, 125, 0.35);
  box-shadow: 0 0 0 8px rgba(20, 184, 166, 0.08);
}

.wizard-stepper-item.active .wizard-stepper-number,
.wizard-stepper-item.complete .wizard-stepper-number {
  background: linear-gradient(135deg, #03967d 0%, #14b8a6 100%);
  border-color: transparent;
  color: #fff;
}

.wizard-stepper-copy {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.wizard-stepper-copy strong {
  font-size: 0.82rem;
  color: #0f172a;
  line-height: 1.1;
}

.wizard-stepper-copy small {
  color: #64748b;
  font-size: 0.72rem;
  line-height: 1.2;
}

.wizard-progress-track {
  width: 100%;
  height: 0.22rem;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.18);
  overflow: hidden;
}

.wizard-progress-fill {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #03967d 0%, #14b8a6 100%);
  transition: width 0.22s ease;
}

.wizard-pane {
  min-height: 0;
}

.wizard-form {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

.wizard-form-page {
  min-height: 0;
}

.wizard-page-layout {
  display: block;
}

.wizard-page-layout--with-summary {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 1.25rem;
  align-items: start;
}

.wizard-page-main {
  min-width: 0;
}

.wizard-body-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 0.2rem;
  scrollbar-gutter: stable;
}

.wizard-body-scroll-page {
  overflow-y: visible;
  padding-right: 0;
}

.wizard-stage {
  display: block;
}

.wizard-card {
  width: 100%;
  border: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
  padding: 0.2rem 0 0;
}

.wizard-card-compact {
  max-width: 720px;
  margin: 0 auto;
}

.wizard-card-service-step {
  max-width: 540px;
  margin: 0;
}

.wizard-card-medium {
  max-width: 100%;
}

.wizard-card-sheet {
  padding-bottom: 1.1rem;
}

.wizard-summary-card {
  border: 1px solid rgba(203, 213, 225, 0.75);
  border-radius: 1.2rem;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.96), rgba(248, 251, 252, 0.98)),
    radial-gradient(circle at top right, rgba(20, 184, 166, 0.14), transparent 42%);
  box-shadow: 0 22px 48px rgba(15, 23, 42, 0.08);
  padding: 1.15rem 1.05rem 1rem;
  min-height: 100%;
}

.wizard-summary-head h6 {
  margin-bottom: 0.35rem;
  color: #0f172a;
  font-size: 1.02rem;
}

.wizard-summary-head p {
  margin-bottom: 0;
  color: #64748b;
  font-size: 0.84rem;
  line-height: 1.5;
}

.wizard-summary-kicker {
  display: inline-flex;
  align-items: center;
  min-height: 1.55rem;
  padding: 0.14rem 0.52rem;
  border-radius: 999px;
  background: rgba(3, 150, 125, 0.1);
  color: #047857;
  font-size: 0.69rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 0.6rem;
}

.wizard-summary-section {
  display: grid;
  gap: 0.85rem;
  margin-top: 1rem;
}

.wizard-summary-row {
  display: grid;
  gap: 0.16rem;
  padding: 0.8rem 0.85rem;
  border-radius: 0.95rem;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(226, 232, 240, 0.9);
}

.wizard-summary-label,
.wizard-summary-stat-label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #64748b;
}

.wizard-summary-value {
  color: #0f172a;
  font-size: 0.96rem;
  line-height: 1.35;
}

.wizard-summary-meta {
  color: #0f766e;
  font-size: 0.8rem;
  font-weight: 600;
}

.wizard-summary-divider {
  height: 1px;
  margin: 1rem 0 0;
  background: linear-gradient(90deg, rgba(203, 213, 225, 0.12), rgba(203, 213, 225, 0.9), rgba(203, 213, 225, 0.12));
}

.wizard-summary-stat-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.8rem;
}

.wizard-summary-stat {
  min-width: 0;
  padding: 0.85rem 0.8rem;
  border-radius: 0.95rem;
  background: rgba(240, 253, 250, 0.9);
  border: 1px solid rgba(153, 246, 228, 0.7);
}

.wizard-summary-stat-value {
  display: block;
  margin-top: 0.35rem;
  color: #0f172a;
  font-size: 0.94rem;
  line-height: 1.4;
}

.wizard-summary-note {
  margin-top: 1rem;
  padding: 0.85rem 0.9rem;
  border-radius: 0.95rem;
  background: rgba(15, 118, 110, 0.08);
  color: #0f766e;
  font-size: 0.84rem;
  line-height: 1.55;
  border: 1px solid rgba(94, 234, 212, 0.35);
}

.wizard-card-head {
  margin-bottom: 1.1rem;
}

.wizard-card-head h6 {
  margin-bottom: 0.3rem;
  color: #0f172a;
  font-size: 1.18rem;
}

.wizard-card-head p {
  margin-bottom: 0;
  color: #5f7088;
  max-width: 720px;
  font-size: 0.96rem;
}

.review-hero {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.05rem;
  border-radius: 1.1rem;
  background:
    linear-gradient(135deg, rgba(240, 253, 250, 0.96), rgba(248, 250, 252, 0.98)),
    radial-gradient(circle at top right, rgba(20, 184, 166, 0.12), transparent 46%);
  border: 1px solid rgba(167, 243, 208, 0.8);
  margin-bottom: 1rem;
}

.review-hero-kicker {
  display: inline-block;
  margin-bottom: 0.35rem;
  font-size: 0.73rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #047857;
}

.review-hero h5 {
  color: #0f172a;
}

.review-hero p {
  color: #52637a;
}

.review-hero-badge {
  display: inline-flex;
  align-items: center;
  min-height: 2rem;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  background: rgba(3, 150, 125, 0.12);
  color: #047857;
  font-size: 0.8rem;
  font-weight: 700;
  white-space: nowrap;
}

.review-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(280px, 320px);
  gap: 1rem;
  align-items: start;
}

.review-main,
.review-side {
  display: grid;
  gap: 1rem;
}

.review-panel {
  border: 1px solid #dbe4f0;
  border-radius: 1.05rem;
  padding: 1rem 1.05rem;
  background: rgba(255, 255, 255, 0.92);
}

.review-panel--accent {
  background: linear-gradient(180deg, rgba(248, 250, 252, 0.98), rgba(240, 253, 250, 0.94));
}

.review-panel-head {
  margin-bottom: 0.95rem;
}

.review-panel-head h6 {
  margin-bottom: 0.3rem;
  color: #0f172a;
}

.review-panel-head p {
  margin-bottom: 0;
  color: #64748b;
  font-size: 0.86rem;
  line-height: 1.5;
}

.review-detail-list,
.review-break-list,
.review-check-list {
  display: grid;
  gap: 0.75rem;
}

.review-pattern-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem;
  align-items: start;
}

.review-detail-row {
  display: grid;
  gap: 0.22rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #edf2f7;
}

.review-detail-row:last-child {
  padding-bottom: 0;
  border-bottom: 0;
}

.review-detail-label {
  font-size: 0.73rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #64748b;
}

.review-detail-value {
  color: #0f172a;
  font-size: 0.97rem;
  line-height: 1.45;
}

.review-pattern-card,
.review-break-item {
  border: 1px solid #e2e8f0;
  border-radius: 0.95rem;
  padding: 0.85rem 0.9rem;
  background: #fff;
  min-height: 100%;
}

.review-pattern-title {
  display: block;
  margin-bottom: 0.4rem;
  color: #0f172a;
}

.review-pattern-line {
  color: #52637a;
  font-size: 0.86rem;
  line-height: 1.5;
}

.review-pattern-line + .review-pattern-line {
  margin-top: 0.25rem;
}

.review-break-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.review-break-index {
  font-size: 0.78rem;
  font-weight: 700;
  color: #0f766e;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.review-empty-state,
.review-notes-hint {
  color: #64748b;
  font-size: 0.86rem;
  line-height: 1.5;
}

.review-check-item {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  color: #0f172a;
  font-weight: 600;
}

.review-check-item i {
  color: #03967d;
  font-size: 1rem;
}

.wizard-card-kicker {
  display: inline-flex;
  align-items: center;
  min-height: 1.7rem;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  background: rgba(3, 150, 125, 0.1);
  color: #047857;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 0.55rem;
}

.time-range-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.9rem;
}

.time-range-grid.two-up {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.one-off-layout {
  display: grid;
  gap: 1rem;
}

.one-off-date-field {
  max-width: 360px;
}

.one-off-time-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  max-width: 720px;
}

.one-off-breaks-block {
  max-width: 720px;
}

.time-range-field {
  padding: 1rem 1.05rem;
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.56);
  border: 1px solid rgba(203, 213, 225, 0.8);
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.time-range-field:focus-within {
  border-color: rgba(3, 150, 125, 0.4);
  box-shadow: 0 12px 28px rgba(3, 150, 125, 0.08);
  transform: translateY(-1px);
}

.time-range-field.invalid {
  border-color: rgba(220, 38, 38, 0.35);
  background: linear-gradient(180deg, rgba(254, 242, 242, 0.96), rgba(255, 255, 255, 0.96));
}

.wizard-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1.2rem;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
  flex-shrink: 0;
  background: linear-gradient(180deg, rgba(248, 251, 253, 0), rgba(248, 251, 253, 0.92) 35%, rgba(248, 251, 253, 0.98) 100%);
  backdrop-filter: blur(8px);
}

.wizard-footer-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.availability-type-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.85rem;
}

.availability-type-card {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 0.95rem 1rem;
  padding-top: 1.35rem;
  border: 1px solid rgba(203, 213, 225, 0.92);
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.availability-type-card.active {
  border-color: #03967d;
  background: rgba(3, 150, 125, 0.08);
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.04);
}

.availability-type-card input {
  display: none;
}

.type-radio {
  width: 1.1rem;
  height: 1.1rem;
  border-radius: 999px;
  border: 2px solid #94a3b8;
  display: inline-flex;
  position: relative;
  flex-shrink: 0;
}

.availability-type-card.active .type-radio {
  border-color: #03967d;
}

.availability-type-card.active .type-radio::after {
  content: "";
  position: absolute;
  inset: 0.18rem;
  border-radius: 999px;
  background: #03967d;
}

.type-copy {
  display: flex;
  flex-direction: column;
}

.type-copy small {
  color: #64748b;
  margin-top: 0.12rem;
}

.availability-pane {
  padding: 1.15rem;
  border: 1px solid #e2e8f0;
  border-radius: 1rem;
  background: #fbfdff;
  margin-bottom: 1rem;
}

.one-off-pane {
  background: linear-gradient(180deg, #fcfefe 0%, #f8fbfd 100%);
}

.recurring-pane {
  border-style: dashed;
}

.recurring-sheet-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem;
  border-radius: 0.9rem;
  background: rgba(3, 150, 125, 0.08);
  margin-bottom: 0.9rem;
}

.recurring-sheet-pane {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.recurring-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.85rem;
}

.one-off-preview-grid {
  margin-top: 0.25rem;
}

.summary-chip {
  border: 1px solid #dbe4f0;
  border-radius: 0.9rem;
  padding: 0.85rem 0.95rem;
  background: rgba(255, 255, 255, 0.54);
}

.summary-chip .label {
  display: block;
  font-size: 0.76rem;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.2rem;
}

.empty-breaks {
  border: 1px dashed #cbd5e1;
  border-radius: 0.9rem;
  padding: 0.9rem 1rem;
  color: #64748b;
  background: #fbfdff;
}

.break-card {
  border: 1px solid #e2e8f0;
  border-radius: 1rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.62);
  margin-top: 0.75rem;
  box-shadow: none;
}

.wizard-fade-enter-active,
.wizard-fade-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}

.wizard-fade-enter,
.wizard-fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

.break-card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.85rem;
}

.break-title {
  color: #0f172a;
  font-size: 0.96rem;
}

.break-remove-btn {
  width: 42px;
  height: 42px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(220, 38, 38, 0.24);
  background: rgba(254, 226, 226, 0.55);
  color: #dc2626;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.break-remove-btn i {
  font-size: 1.15rem;
  line-height: 1;
}

.availability-type-card.stacked {
  min-height: 86px;
}

.availability-option-badge {
  position: absolute;
  top: 0.7rem;
  right: 0.85rem;
  display: inline-flex;
  align-items: center;
  min-height: 1.3rem;
  padding: 0.08rem 0.45rem;
  border-radius: 999px;
  background: rgba(3, 150, 125, 0.12);
  color: #047857;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.break-remove-btn:hover {
  background: rgba(254, 202, 202, 0.9);
  border-color: rgba(220, 38, 38, 0.38);
  color: #b91c1c;
}

.sheet-legend {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  font-size: 0.9rem;
  color: #475569;
}

.break-guidance-panel {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.1rem;
  border-radius: 1rem;
  border: 1px solid rgba(249, 115, 22, 0.28);
  background:
    linear-gradient(135deg, rgba(255, 247, 237, 0.98), rgba(255, 251, 235, 0.96)),
    radial-gradient(circle at top right, rgba(251, 146, 60, 0.14), transparent 45%);
  box-shadow: 0 10px 30px rgba(249, 115, 22, 0.08);
}

.break-guidance-copy {
  max-width: 720px;
  color: #7c2d12;
}

.break-guidance-copy p {
  color: #9a3412;
}

.break-guidance-eyebrow {
  display: inline-block;
  font-size: 0.73rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #ea580c;
  margin-bottom: 0.35rem;
}

.break-guidance-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
  justify-content: flex-end;
}

.break-guidance-chip {
  display: inline-flex;
  align-items: center;
  min-height: 2rem;
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(251, 146, 60, 0.24);
  color: #9a3412;
  font-size: 0.82rem;
  font-weight: 600;
}

.break-feedback {
  font-size: 0.82rem;
  line-height: 1.5;
}

.break-feedback--empty {
  color: #d97706;
}

.break-feedback--invalid {
  color: #dc2626;
}

.break-feedback--valid {
  color: #15803d;
}

.break-feedback-success {
  display: inline-flex;
  align-items: center;
  gap: 0.38rem;
  font-weight: 600;
}

.break-feedback-success-icon {
  font-size: 1rem;
  animation: breakTickPulse 1.2s ease-in-out infinite;
}

.legend-box {
  width: 0.85rem;
  height: 0.85rem;
  border-radius: 0.25rem;
  display: inline-block;
  margin-right: 0.35rem;
  vertical-align: middle;
  border: 1px solid #cbd5e1;
}

.legend-box.free {
  background: #fff;
}

.legend-box.occupied {
  background: rgba(220, 38, 38, 0.12);
  border-color: rgba(220, 38, 38, 0.4);
}

.legend-box.availability {
  background: rgba(3, 150, 125, 0.18);
  border-color: rgba(3, 150, 125, 0.45);
}

.legend-box.break {
  background: rgba(249, 115, 22, 0.16);
  border-color: rgba(249, 115, 22, 0.45);
}

.break-mode-toggle {
  border-radius: 999px;
  font-weight: 600;
}

.break-mode-toggle.needs-attention {
  animation: breakAttentionPulse 1.5s ease-in-out infinite;
  box-shadow: 0 0 0 0 rgba(249, 115, 22, 0.45);
}

.break-mode-toggle.btn-warning {
  box-shadow: 0 10px 24px rgba(249, 115, 22, 0.2);
}

.recurring-sheet-scroll {
  overflow-x: auto;
  border: 1px solid #e2e8f0;
  border-radius: 1rem;
  background: #fff;
}

.recurring-sheet-table {
  width: 100%;
  min-width: 980px;
  border-collapse: separate;
  border-spacing: 0;
}

.recurring-sheet-table th,
.recurring-sheet-table td {
  padding: 0.55rem;
  border-bottom: 1px solid #eef2f7;
  border-right: 1px solid #eef2f7;
}

.recurring-sheet-table tbody th {
  background: #fbfdff;
  min-width: 98px;
  color: #0f172a;
  position: sticky;
  left: 0;
  z-index: 1;
}

.day-header-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.35rem;
  margin-bottom: 0.2rem;
}

.day-break-actions {
  display: flex;
  align-items: center;
}

.day-break-action-btn {
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 700;
  line-height: 1.1;
  padding: 0.24rem 0.52rem;
  min-height: 1.8rem;
}

.recurring-sheet-table tbody tr.day-warning-row td {
  background: linear-gradient(180deg, rgba(255, 251, 235, 0.96), rgba(255, 247, 237, 0.92));
}

.recurring-sheet-table tbody tr.day-break-focus-row td {
  background: linear-gradient(180deg, rgba(255, 247, 237, 1), rgba(255, 237, 213, 0.96));
}

.recurring-sheet-table tbody th.day-warning-header {
  background: linear-gradient(180deg, rgba(255, 247, 237, 1), rgba(255, 251, 235, 1));
  border-right-color: rgba(249, 115, 22, 0.18);
}

.day-break-usage.warning {
  display: block;
  color: #c2410c;
  font-weight: 600;
  font-size: 0.75rem;
}

.day-break-warning {
  display: block;
  margin-top: 0.18rem;
  color: #c2410c;
  font-size: 0.71rem;
  font-weight: 600;
  line-height: 1.2;
}

.sheet-cell-hour {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.2rem;
  min-width: 58px;
}

.sheet-hour {
  grid-column: 1 / -1;
  width: 100%;
  height: 48px;
  border: 1px solid #dbe4f0;
  border-radius: 0.7rem;
  background: #fff;
  transition: all 0.18s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sheet-hour.free:hover {
  border-color: #03967d;
  background: rgba(3, 150, 125, 0.05);
}

.sheet-hour.availability {
  background: rgba(3, 150, 125, 0.22);
  border-color: rgba(3, 150, 125, 0.55);
}

.sheet-hour.occupied {
  background: rgba(254, 226, 226, 0.9);
  border-color: rgba(220, 38, 38, 0.35);
  cursor: not-allowed;
}

.sheet-hour-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #334155;
}

.sheet-break-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 100%;
  padding: 0.2rem 0.35rem;
  border-radius: 0.45rem;
  background: rgba(249, 115, 22, 0.14);
  color: #c2410c;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.sheet-quarter {
  width: 100%;
  height: 48px;
  border: 1px solid #dbe4f0;
  border-radius: 0.5rem;
  background: #fff;
  transition: all 0.18s ease;
}

.sheet-quarter.free:hover {
  border-color: #03967d;
  background: rgba(3, 150, 125, 0.05);
}

.sheet-quarter.occupied {
  background: rgba(254, 226, 226, 0.9);
  border-color: rgba(220, 38, 38, 0.35);
  cursor: not-allowed;
}

.sheet-quarter.availability {
  background: rgba(3, 150, 125, 0.22);
  border-color: rgba(3, 150, 125, 0.55);
}

.sheet-quarter.break {
  background: rgba(251, 146, 60, 0.24);
  border-color: rgba(249, 115, 22, 0.55);
}

@keyframes breakAttentionPulse {
  0% {
    box-shadow: 0 0 0 0 rgba(249, 115, 22, 0.38);
    transform: translateY(0);
  }
  50% {
    box-shadow: 0 0 0 12px rgba(249, 115, 22, 0);
    transform: translateY(-1px);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(249, 115, 22, 0);
    transform: translateY(0);
  }
}

@keyframes breakTickPulse {
  0% {
    transform: scale(0.94);
    opacity: 0.72;
  }
  50% {
    transform: scale(1.08);
    opacity: 1;
  }
  100% {
    transform: scale(0.94);
    opacity: 0.72;
  }
}

.weekly-summary-list {
  display: grid;
  gap: 0.75rem;
}

.weekly-summary-card {
  border: 1px solid #dbe4f0;
  border-radius: 0.9rem;
  background: #fff;
  padding: 0.9rem 1rem;
}

.table-head {
  align-items: center;
}

.table-tools {
  min-width: 270px;
}

.schedule-cell strong {
  display: block;
  color: #0f172a;
}

.table-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1rem;
}

.calendar-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.calendar-shell {
  display: grid;
  grid-template-columns: minmax(360px, 1.1fr) minmax(360px, 1.2fr);
  gap: 1rem;
  align-items: stretch;
}

.calendar-pane,
.slots-pane {
  min-height: 560px;
  max-height: 560px;
  border: 1px solid #e2e8f0;
  border-radius: 1rem;
  background: #fbfdff;
}

.calendar-pane {
  padding: 1rem;
}

.slots-pane {
  padding: 1rem;
  display: flex;
  flex-direction: column;
}

.slots-pane-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.85rem;
}

.slots-state {
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #1d4ed8;
}

.slots-state.empty {
  color: #64748b;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 0.5rem;
}

.calendar-head {
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-align: center;
}

.calendar-cell {
  min-height: 78px;
  border: 1px solid #dbe4f0;
  border-radius: 0.85rem;
  background: #fff;
  padding: 0.55rem;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  position: relative;
}

.calendar-cell.touched {
  border-color: rgba(3, 150, 125, 0.35);
  background: rgba(3, 150, 125, 0.06);
}

.calendar-cell.booked {
  box-shadow: inset 0 0 0 1px rgba(220, 38, 38, 0.16);
}

.calendar-cell.active {
  border-color: #03967d;
  box-shadow: 0 0 0 1px rgba(3, 150, 125, 0.25);
}

.calendar-cell.today:not(.active) {
  border-color: rgba(3, 150, 125, 0.42);
  background: linear-gradient(180deg, rgba(240, 253, 250, 0.96), rgba(255, 255, 255, 0.98));
  box-shadow: inset 0 0 0 1px rgba(3, 150, 125, 0.12);
}

.calendar-cell.today .day-number {
  color: #047857;
}

.calendar-cell.muted {
  opacity: 0.45;
}

.day-number {
  font-weight: 700;
  color: #1e293b;
}

.calendar-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.8rem;
  width: fit-content;
  padding: 0.14rem 0.45rem;
  border-radius: 999px;
  background: #03967d;
  color: #fff;
  font-size: 0.72rem;
}

.calendar-booked-dot {
  position: absolute;
  top: 0.6rem;
  right: 0.6rem;
  width: 0.6rem;
  height: 0.6rem;
  border-radius: 999px;
  background: #dc2626;
}

.slot-list {
  display: grid;
  gap: 0.75rem;
  overflow-y: auto;
  padding-right: 0.25rem;
}

.slot-card {
  border: 1px solid #e2e8f0;
  border-radius: 0.95rem;
  padding: 0.9rem;
  background: #fff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.slot-card--booked {
  border-color: rgba(220, 38, 38, 0.18);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(254, 242, 242, 0.65));
}

.slot-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 16px 34px rgba(15, 23, 42, 0.06);
}

.slot-card-foot {
  display: flex;
  align-items: center;
}

.slot-booking-pill {
  display: inline-flex;
  align-items: center;
  min-height: 1.85rem;
  padding: 0.2rem 0.65rem;
  border-radius: 999px;
  background: rgba(220, 38, 38, 0.08);
  color: #b91c1c;
  font-size: 0.76rem;
  font-weight: 700;
}

:deep(.slot-booking-popover .popover) {
  border: 1px solid rgba(16, 185, 129, 0.3);
  border-radius: 1rem;
  box-shadow:
    0 24px 54px rgba(6, 95, 70, 0.18),
    0 0 0 1px rgba(255, 255, 255, 0.88);
  max-width: 380px;
  background: #ffffff;
  overflow: hidden;
}

:deep(.slot-booking-popover.bs-popover-auto[x-placement^="top"] .arrow::before),
:deep(.slot-booking-popover.bs-popover-top .arrow::before) {
  border-top-color: rgba(16, 185, 129, 0.3);
}

:deep(.slot-booking-popover.bs-popover-auto[x-placement^="bottom"] .arrow::before),
:deep(.slot-booking-popover.bs-popover-bottom .arrow::before) {
  border-bottom-color: rgba(16, 185, 129, 0.3);
}

:deep(.slot-booking-popover.bs-popover-auto[x-placement^="left"] .arrow::before),
:deep(.slot-booking-popover.bs-popover-left .arrow::before) {
  border-left-color: rgba(16, 185, 129, 0.3);
}

:deep(.slot-booking-popover.bs-popover-auto[x-placement^="right"] .arrow::before),
:deep(.slot-booking-popover.bs-popover-right .arrow::before) {
  border-right-color: rgba(16, 185, 129, 0.3);
}

:deep(.slot-booking-popover .popover-body) {
  padding: 0;
}

.slot-booking-popover-body {
  padding: 1rem 1.05rem 1.05rem;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 1), rgba(248, 250, 252, 0.98)),
    radial-gradient(circle at top right, rgba(16, 185, 129, 0.14), transparent 44%);
  border-top: 4px solid rgba(5, 150, 105, 0.72);
}

.slot-booking-popover-head {
  display: grid;
  gap: 0.15rem;
  margin-bottom: 0.6rem;
}

.slot-booking-popover-kicker {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #047857;
}

.slot-booking-popover-service {
  margin-bottom: 0.75rem;
  color: #475569;
  font-size: 0.85rem;
}

.slot-booking-popover-grid {
  display: grid;
  gap: 0.55rem;
}

.slot-booking-popover-row {
  display: grid;
  gap: 0.15rem;
  padding-bottom: 0.55rem;
  border-bottom: 1px solid #eef2f7;
}

.slot-booking-popover-row:last-child {
  padding-bottom: 0;
  border-bottom: 0;
}

.slot-booking-popover-label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #64748b;
}

.slot-booking-popover-value {
  color: #0f172a;
  font-size: 0.9rem;
  line-height: 1.45;
}

.slot-booking-popover-empty {
  color: #64748b;
  font-size: 0.85rem;
  line-height: 1.5;
}

.is-invalid-field :deep(.multiselect__tags) {
  border-color: #dc3545 !important;
}

@media (max-width: 1199.98px) {
  .create-availability-modal :deep(.modal-dialog) {
    max-width: min(940px, calc(100vw - 1.5rem));
  }

  .wizard-progress-top {
    flex-direction: column;
  }

  .wizard-service-chip {
    min-width: 0;
    max-width: 100%;
    width: 100%;
  }

  .wizard-page-layout--with-summary {
    grid-template-columns: 1fr;
  }

  .wizard-summary-card {
    min-height: 0;
  }

  .review-layout {
    grid-template-columns: 1fr;
  }

  .wizard-stepper-copy small {
    display: none;
  }

  .calendar-shell {
    grid-template-columns: 1fr;
  }

  .calendar-pane,
  .slots-pane {
    min-height: auto;
    max-height: none;
  }
}

@media (max-width: 767.98px) {
  .create-availability-modal :deep(.modal-dialog) {
    max-width: 100vw;
    margin: 0;
    min-height: 100vh;
  }

  .create-availability-modal :deep(.modal-content) {
    min-height: 100vh;
    max-height: 100vh;
    border-radius: 0;
  }

  .create-availability-modal :deep(.modal-body) {
    padding: 1rem 0.95rem 1rem;
  }

  .wizard-progress {
    gap: 0.8rem;
  }

  .wizard-stepper {
    overflow-x: auto;
    justify-content: flex-start;
    padding-bottom: 0.35rem;
  }

  .wizard-stepper-item {
    flex: 0 0 152px;
  }

  .recurring-sheet-card {
    flex-direction: column;
    align-items: stretch;
  }

  .recurring-summary {
    grid-template-columns: 1fr;
  }

  .review-hero,
  .review-break-item {
    flex-direction: column;
    align-items: flex-start;
  }

  .review-pattern-list {
    grid-template-columns: 1fr;
  }

  .table-footer,
  .section-head,
  .wizard-footer {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }

  .table-tools {
    min-width: 100%;
  }

  .break-card-head {
    align-items: center;
  }

  .wizard-footer-actions {
    width: 100%;
    justify-content: flex-end;
  }

  .wizard-summary-stat-grid {
    grid-template-columns: 1fr;
  }

  .wizard-footer-actions .btn,
  .wizard-footer > .btn {
    width: 100%;
  }
}
</style>
